// src/components/admin/AdminPage.jsx
// FULLY UPDATED: Fixed Visibility Sync with Manages Page.
// Robust visibility checks ensure that Inventory, HR, and other modules appear correctly when enabled.
// Universal Nested Drag and Drop is fully functional.
import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import CurrencySymbol, { resolveCurrencyCode, CURRENCY_SYMBOLS } from '../CurrencySymbol';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './adminPageThemes.css';
import ManagerConsoleDashboard from './ManagerConsoleDashboard';
import {
  FaHome,
  FaMoneyBill,
  FaFileAlt,
  FaChartBar,
  FaDatabase,
  FaCog,
  FaUsers,
  FaBox,
  FaPlusCircle,
  FaTable,
  FaUtensils,
  FaLayerGroup,
  FaUserTie,
  FaEnvelope,
  FaShoppingCart,
  FaSearch,
  FaPrint,
  FaGift,
  FaSignOutAlt,
  FaBuilding,
  FaCamera,
  FaTrash,
  FaChartLine,
  FaFileInvoiceDollar,
  FaEyeSlash,
  FaList,
  FaClock,
  FaBriefcase,
  FaIdCard,
  FaCalendar,
  FaUserPlus,
  FaEye,
  FaCreditCard,
  FaPaperPlane,
  FaMoneyCheckAlt,
  FaCar,
  FaCalendarCheck,
  FaMapMarkerAlt,
  FaBell,
  FaCashRegister,
  FaTabletAlt,
  FaHistory,
  FaConciergeBell,
  FaGripVertical,
  FaUserShield,
  FaImage,
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
  FaCircle,
  FaCalendarAlt,
  FaPalette,
  FaSun,
  FaMoon,
  FaWater,
  FaCloudSun,
  FaWhatsapp,
  FaMagic,
  FaTimes,
} from 'react-icons/fa';
import { UserContext } from '../../Context/UserContext';

const DashboardTimePicker = ({ timeframe, selectedDate, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState(() => {
    if (timeframe === 'Year') return 'years';
    if (timeframe === 'Month') return 'months';
    return 'days';
  });

  const [yearPage, setYearPage] = useState(() => Math.floor(selectedDate.getFullYear() / 12) * 12);
  const [currentViewDate, setCurrentViewDate] = useState(selectedDate);

  useEffect(() => {
    setCurrentViewDate(selectedDate);
    setYearPage(Math.floor(selectedDate.getFullYear() / 12) * 12);
    if (timeframe === 'Year') setView('years');
    else if (timeframe === 'Month') setView('months');
    else setView('days');
    setIsOpen(false);
  }, [selectedDate, timeframe]);

  if (!['Week', 'Month', 'Year'].includes(timeframe)) return null;

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const getDisplayText = () => {
    if (timeframe === 'Year') return selectedDate.getFullYear().toString();
    if (timeframe === 'Month') return `${monthNames[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`;
    if (timeframe === 'Week') {
      const d = new Date(selectedDate);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const startOfWeek = new Date(d.setDate(diff));
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return `${startOfWeek.getDate()} ${monthNames[startOfWeek.getMonth()].substring(0, 3)} - ${endOfWeek.getDate()} ${monthNames[endOfWeek.getMonth()].substring(0, 3)}, ${endOfWeek.getFullYear()}`;
    }
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    if (view === 'days') setCurrentViewDate(new Date(currentViewDate.getFullYear(), currentViewDate.getMonth() - 1, 1));
    else if (view === 'months') setCurrentViewDate(new Date(currentViewDate.getFullYear() - 1, currentViewDate.getMonth(), 1));
    else if (view === 'years') setYearPage(yearPage - 12);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (view === 'days') setCurrentViewDate(new Date(currentViewDate.getFullYear(), currentViewDate.getMonth() + 1, 1));
    else if (view === 'months') setCurrentViewDate(new Date(currentViewDate.getFullYear() + 1, currentViewDate.getMonth(), 1));
    else if (view === 'years') setYearPage(yearPage + 12);
  };

  const year = currentViewDate.getFullYear();
  const month = currentViewDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  let startOffset = firstDayOfMonth.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const selectDay = (day, e) => {
    e.stopPropagation();
    onChange(new Date(year, month, day));
    setIsOpen(false);
  };

  const selectMonth = (idx, e) => {
    e.stopPropagation();
    if (timeframe === 'Month') {
      onChange(new Date(year, idx, 1));
      setIsOpen(false);
    } else {
      setCurrentViewDate(new Date(year, idx, 1));
      setView('days');
    }
  };

  const selectYear = (y, e) => {
    e.stopPropagation();
    if (timeframe === 'Year') {
      onChange(new Date(y, 0, 1));
      setIsOpen(false);
    } else {
      setCurrentViewDate(new Date(y, month, 1));
      setView(timeframe === 'Month' ? 'months' : 'days');
    }
  };

  return (
    <div style={{ position: 'relative', width: '280px', marginTop: '12px' }}>
      <div onClick={() => setIsOpen(!isOpen)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--ap-card)', borderRadius: '12px', border: '1px solid var(--ap-border)', cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
        <span style={{ fontWeight: '600', color: 'var(--ap-text-primary)', fontSize: '15px' }}>{getDisplayText()}</span>
        <FaCalendarAlt style={{ color: 'var(--ap-primary-color)' }} />
      </div>

      {isOpen && (
        <>
          <div onClick={() => setIsOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }} />
          <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: '320px', backgroundColor: 'var(--ap-card)', backdropFilter: 'blur(40px)', borderRadius: '20px', boxShadow: '0 25px 60px rgba(0,0,0,0.6)', padding: '24px', zIndex: 99999, border: '1px solid var(--ap-border)', animation: 'slideDown 0.2s ease', boxSizing: 'border-box' }}>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <button onClick={handlePrev} style={{ width: '36px', height: '36px', background: 'var(--ap-bg)', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--ap-text-primary)' }}>
                <FaChevronLeft />
              </button>

              <div style={{ display: 'flex', gap: '8px' }}>
                {view === 'days' && (
                  <>
                    <span onClick={(e) => { e.stopPropagation(); setView('months'); }} style={{ fontWeight: '700', fontSize: '16px', color: 'var(--ap-text-primary)', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' }} onMouseEnter={(e) => e.target.style.background = 'var(--ap-bg)'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                      {monthNames[month]}
                    </span>
                    <span onClick={(e) => { e.stopPropagation(); setYearPage(Math.floor(year / 12) * 12); setView('years'); }} style={{ fontWeight: '700', fontSize: '16px', color: 'var(--ap-text-primary)', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' }} onMouseEnter={(e) => e.target.style.background = 'var(--ap-bg)'} onMouseLeave={(e) => e.target.style.background = 'transparent'}>
                      {year}
                    </span>
                  </>
                )}
                {view === 'months' && (
                  <span onClick={(e) => { e.stopPropagation(); setYearPage(Math.floor(year / 12) * 12); setView('years'); }} style={{ fontWeight: '700', fontSize: '16px', color: 'var(--ap-text-primary)', cursor: 'pointer' }}>
                    {year}
                  </span>
                )}
                {view === 'years' && (
                  <span style={{ fontWeight: '700', fontSize: '16px', color: 'var(--ap-text-primary)' }}>
                    {yearPage} - {yearPage + 11}
                  </span>
                )}
              </div>

              <button onClick={handleNext} style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--ap-text-primary)' }}>
                <FaChevronRight />
              </button>
            </div>

            {view === 'days' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginBottom: '16px' }}>
                  {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((w, i) => (
                    <span key={i} style={{ fontSize: '13px', fontWeight: '700', color: 'var(--ap-text-primary)' }}>{w}</span>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px 4px' }}>
                  {Array.from({ length: startOffset }).map((_, i) => <div key={`empty-${i}`} />)}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const isSelected = selectedDate.getFullYear() === year && selectedDate.getMonth() === month && selectedDate.getDate() === day;
                    return (
                      <button
                        key={day}
                        onClick={(e) => selectDay(day, e)}
                        style={{ height: '36px', width: '100%', border: 'none', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: isSelected ? '700' : '600', cursor: 'pointer', background: isSelected ? 'var(--ap-primary-color)' : 'transparent', color: isSelected ? 'var(--ap-card)' : 'var(--ap-text-primary)', transition: 'all 0.2s ease' }}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {view === 'months' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                {monthNames.map((mName, index) => {
                  const isSelected = selectedDate.getFullYear() === year && selectedDate.getMonth() === index;
                  return (
                    <button
                      key={mName}
                      onClick={(e) => selectMonth(index, e)}
                      style={{ height: '42px', border: 'none', borderRadius: '10px', background: isSelected ? 'var(--ap-primary-color)' : 'var(--ap-bg)', color: isSelected ? 'var(--ap-card)' : 'var(--ap-text-primary)', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}
                    >
                      {mName.substring(0, 3)}
                    </button>
                  );
                })}
              </div>
            )}

            {view === 'years' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                {Array.from({ length: 12 }).map((_, i) => {
                  const y = yearPage + i;
                  const isSelected = selectedDate.getFullYear() === y;
                  return (
                    <button
                      key={y}
                      onClick={(e) => selectYear(y, e)}
                      style={{ height: '42px', border: 'none', borderRadius: '10px', background: isSelected ? 'var(--ap-primary-color)' : 'var(--ap-bg)', color: isSelected ? 'var(--ap-card)' : 'var(--ap-text-primary)', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}
                    >
                      {y}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};


function AdminPage() {
  const { user, baseUrl, configLoading, getHeaders } = useContext(UserContext);
  const [workflowSettings, setWorkflowSettings] = useState({});
  const [disabledPages, setDisabledPages] = useState([]);
  const [activeCompany, setActiveCompany] = useState(() => {
    const profileCompany = user?.company_name || user?.company;
    const isGlobal = (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'groupadmin' || (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'superadmin' || (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'tenantadmin' || (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'masteradmin';
    if (!isGlobal && profileCompany && profileCompany.toLowerCase() !== 'all') {
      return profileCompany;
    }
    return localStorage.getItem('active_company') || '';
  });
  const [activeBranch, setActiveBranch] = useState(() => {
    const profileBranch = user?.branch_name || user?.branch;
    const isGlobal = (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'groupadmin' || (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'superadmin' || (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'tenantadmin' || (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'masteradmin';
    const isGlobalForBranch = isGlobal || (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'companyadmin';
    if (!isGlobalForBranch && profileBranch && profileBranch.toLowerCase() !== 'all' && profileBranch !== 'All Branches') {
      return profileBranch;
    }
    return localStorage.getItem('active_branch') || 'All Branches';
  });
  const [adminCompanies, setAdminCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [customerCount, setCustomerCount] = useState(0);
  const [itemCount, setItemCount] = useState(0);
  const [backupCount, setBackupCount] = useState(0);
  const [salesData, setSalesData] = useState([]);
  const [purchaseInvoices, setPurchaseInvoices] = useState([]);
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stockBalanceData, setStockBalanceData] = useState([]);
  const [stockLedgerData, setStockLedgerData] = useState([]);
  const [itemsData, setItemsData] = useState([]);      // From /api/items — for images & groups
  const [activeOrdersData, setActiveOrdersData] = useState([]); // From /api/activeorders
  const [customersData, setCustomersData] = useState([]); // From /api/customers
  const [posSessionsData, setPosSessionsData] = useState([]); // From /api/session-history (Opening & Closing)
  const [salesLoading, setSalesLoading] = useState(false);
  const [timeframe, setTimeframe] = useState('Today'); // 'Yesterday', 'Today', 'Week', 'Month', 'Year'
  const [selectedDashboardDate, setSelectedDashboardDate] = useState(new Date());
  const [hoveredDonutGroup, setHoveredDonutGroup] = useState(null);
  const [donutGroupPos, setDonutGroupPos] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMasterOpen, setIsMasterOpen] = useState(true);
  const [isCustomersOpen, setIsCustomersOpen] = useState(false);
  const [isStockOpen, setIsStockOpen] = useState(false);
  const [isItemsOpen, setIsItemsOpen] = useState(false);
  const [isEmployeeAppOpen, setIsEmployeeAppOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSalesOpen, setIsSalesOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [isAttendanceOpen, setIsAttendanceOpen] = useState(false);
  const [isLeaveOpen, setIsLeaveOpen] = useState(false);
  const [isSalaryOpen, setIsSalaryOpen] = useState(false);
  const [isAddressOpen, setIsAddressOpen] = useState(false);
  const [isMultiCompanyOpen, setIsMultiCompanyOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showImportSuccess, setShowImportSuccess] = useState(false);
  const [importStats, setImportStats] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [message, setMessage] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [settings, setSettings] = useState({ dateFormat: 'dd-mm-yyyy', timeFormat: 'hh:mm a' });
  const [unreadCount, setUnreadCount] = useState(0);
  const [filteredMasterMenuItems, setFilteredMasterMenuItems] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isBoxViewActive, setIsBoxViewActive] = useState(false);
  const [boxViewActiveParent, setBoxViewActiveParent] = useState(null);
  const [activeModernParent, setActiveModernParent] = useState(null);
  const [boxViewSearchQuery, setBoxViewSearchQuery] = useState('');
  const [dashboardViewType, setDashboardViewType] = useState('salesreport');

  // Setup Prompt State
  const [kitchensExist, setKitchensExist] = useState(true);
  const [groupsExist, setGroupsExist] = useState(true);
  const [typesExist, setTypesExist] = useState(true);
  const [departmentsExist, setDepartmentsExist] = useState(true);
  const [designationsExist, setDesignationsExist] = useState(true);
  const [showSetupPrompt, setShowSetupPrompt] = useState(false);
  const [setupStep, setSetupStep] = useState(0); // 1=Kitchen, 2=Addon, 3=Type, 4=Dept, 5=Designation
  const [pendingPath, setPendingPath] = useState(null);

  // Theme State
  const [theme, setTheme] = useState(() => localStorage.getItem('appTheme') || 'light');
  const [layoutMode, setLayoutMode] = useState(() => localStorage.getItem('adminLayoutMode') || 'classic');
  const [adminDesign, setAdminDesign] = useState(() => localStorage.getItem('adminDesignVersion') || 'design1');
  const [showThemeDropdown, setShowThemeDropdown] = useState(false);
  const themeDropdownRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('appTheme', theme);
    document.body.className = `theme-${theme}`;
    return () => {
      document.body.className = '';
    };
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('adminLayoutMode', layoutMode);
  }, [layoutMode]);

  useEffect(() => {
    localStorage.setItem('adminDesignVersion', adminDesign);
  }, [adminDesign]);

  // Handle clicking outside theme dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (themeDropdownRef.current && !themeDropdownRef.current.contains(event.target)) {
        setShowThemeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigate = useNavigate();

  const pathMap = {
    "/home": { mod: "pos_billing", page: "HomeDashboard" },
    "/salespage": { mod: "reports", page: "SalesInvoice" },
    "/cash": { mod: "pos_billing", page: "CashPayment" },
    "/card": { mod: "pos_billing", page: "CardPayment" },
    "/savedorders": { mod: "pos_billing", page: "SavedOrders" },
    "/pos-balance": { mod: "pos_billing", page: "POSBalance" },
    "/combo-offer": { mod: "inventory", page: "ComboOffer" },
    "/active-orders": { mod: "tracking", page: "ActiveOrders" },
    "/kitchen": { mod: "kitchen_display", page: "KitchenView" },
    "/add-kitchen": { mod: "kitchen_display", page: "AddKitchen" },
    "/items": { mod: "inventory", page: "ItemsList" },
    "/items-list": { mod: "inventory", page: "ItemsList" },
    "/inventory": { mod: "inventory", page: "ItemsList" },
    "/create-item": { mod: "inventory", page: "CreateItem" },
    "/add-item": { mod: "inventory", page: "CreateItem" },
    "/add-item-group": { mod: "inventory", page: "AddItemGroup" },
    "/add-ingredients-nutrition": { mod: "inventory", page: "AddIngredients&Nutrition" },
    "/create-variant": { mod: "inventory", page: "CreateVariant" },
    "/menu-timings": { mod: "inventory", page: "ItemsList" },
    "/hidden-items": { mod: "inventory", page: "HiddenItems" },
    "/rewards": { mod: "inventory", page: "Rewards" },
    "/create-reward": { mod: "inventory", page: "CreateReward" },
    "/reward-settings": { mod: "inventory", page: "RewardSettings" },
    "/purchase": { mod: "purchase", page: "Purchase" },
    "/add-employee": { mod: "hr_payroll", page: "AddEmployee" },
    "/employee-list": { mod: "hr_payroll", page: "EmployeeList" },
    "/employees": { mod: "hr_payroll", page: "DeliveryPersons" },
    "/employee-details/:id": { mod: "hr_payroll", page: "EmployeeDetails" },
    "/users": { mod: "hr_payroll", page: "Users" },
    "/employee-designations": { mod: "hr_payroll", page: "EmployeeDesignations" },
    "/employee-types": { mod: "hr_payroll", page: "EmployeeTypes" },
    "/employee-departments": { mod: "hr_payroll", page: "EmployeeDepartments" },
    "/attendance": { mod: "hr_payroll", page: "Attendance" },
    "/attendance-view": { mod: "hr_payroll", page: "AttendanceView" },
    "/salary-slip": { mod: "hr_payroll", page: "SalarySlip" },
    "/salary-receipt-list": { mod: "hr_payroll", page: "SalaryReceiptList" },
    "/leave-type": { mod: "hr_payroll", page: "LeaveType" },
    "/leave-allocation": { mod: "hr_payroll", page: "LeaveAllocation" },
    "/leave-apply": { mod: "hr_payroll", page: "LeaveApply" },
    "/schedule-master": { mod: "hr_payroll", page: "ScheduleMaster" },
    "/schedule-assign-employee": { mod: "hr_payroll", page: "ScheduleAssignEmployee" },
    "/roaster": { mod: "hr_payroll", page: "Roaster" },
    "/holiday-doctype": { mod: "hr_payroll", page: "HolidayList" },
    "/extended-doctype": { mod: "hr_payroll", page: "ExtendedDocType" },
    "/import-data": { mod: "settings", page: "AdvancedImport" },
    "/record": { mod: "admin_dashboard", page: "AuditRecords" },
    "/admin-dashboard": { mod: "admin_dashboard", page: "AdminDashboard" },
    "/customers": { mod: "customer_module", page: "CustomerList" },
    "/create-customer": { mod: "customer_module", page: "CreateCustomer" },
    "/create-customer-group": { mod: "customer_module", page: "CustomerGroup" },
    "/bearer": { mod: "pos_billing", page: "BearerPage" },
    "/opening-entry": { mod: "pos_billing", page: "OpeningEntry" },
    "/closing-entry": { mod: "pos_billing", page: "ClosingEntry" },
    "/add-table": { mod: "table_management", page: "AddTable" },
    "/table": { mod: "table_management", page: "TableView" },
    "/frontpage": { mod: "table_management", page: "FrontPage" },
    "/booking": { mod: "table_management", page: "Booking" },
    "/vehicle-management": { mod: "vehicle_module", page: "VehicleManagement" },
    "/vehicle-management-page": { mod: "vehicle_module", page: "VehicleManagement" },
    "/address-structure": { mod: "address_module", page: "AddressStructure" },
    "/address-structure-page": { mod: "address_module", page: "AddressStructure" },
    "/sales-reports": { mod: "reports", page: "SalesReport" },
    "/trip-report": { mod: "reports", page: "TripReport" },
    "/sales-kanban": { mod: "reports", page: "SalesKanban" },
    "/system-settings": { mod: "settings", page: "SystemSettings" },
    "/doctype": { mod: "settings", page: "DocTypeManagement" },
    "/email-settings": { mod: "settings", page: "EmailSettings" },
    "/print-settings": { mod: "settings", page: "PrintSettings" },
    "/backup": { mod: "settings", page: "Backups" },
    "/working": { mod: "settings", page: "WorkingDays" },
    "/tax-master": { mod: "settings", page: "TaxMaster" },
    "/company-details": { mod: "multi_branch", page: "CompanyDetails" },
    "/manage-branches": { mod: "multi_branch", page: "ManageBranches" },
    "/manage-companies": { mod: "multi_branch", page: "ManageCompanies" },
    "/role-permissions": { mod: "roles", page: "RolePermissions" },
    "/notifications": { mod: "notifications", page: "Notifications" },
    "/voice-support": { mod: "notifications", page: "VoiceSupport" },
    "/voice": { mod: "notifications", page: "VoiceSupport" },
    // Sidebar Parent Node Mappings
    "Items": { mod: "inventory" },
    "Inventory": { mod: "inventory" },
    "Employee": { mod: "hr_payroll" },
    "HR & Payroll": { mod: "hr_payroll" },
    "Settings": { mod: "settings" },
    "Multi-Branch Support": { mod: "multi_branch" },
    "Purchase": { mod: "purchase" },
    "Reports": { mod: "reports" },
    "Table Management": { mod: "table_management" },
    "Customer Module": { mod: "customer_module" },
    "POS Billing": { mod: "pos_billing" },
    "Kitchen Display": { mod: "kitchen_display" }
  };

  const checkIsAdmin = (roleStr) => {
    const userStored = localStorage.getItem('user');
    const u = userStored ? JSON.parse(userStored) : {};
    const role = roleStr || u.role || u.user_role || u.role_name || "";
    const norm = String(role).toLowerCase().replace(/[\s_]/g, '');
    const adminRoles = [
      'admin', 'superadmin', 'tenantadmin', 'companyadmin', 'groupadmin',
      'branchadmin', 'branchmanager', 'manager', 'owner',
      'restaurantmanager', 'shopmanager', 'generalmanager',
      'branch', 'branchmanager', 'user', 'staff'
    ];
    return adminRoles.includes(norm) || norm.includes('admin') || norm.includes('super') || norm.includes('manager') || norm.includes('owner') || norm.includes('branch');
  };

  const isPageEnabled = (pathOrModule, userRole) => {
    let modKey;
    let pageKey = null;
    const isPath = pathOrModule.startsWith('/');
    if (isPath) {
      if (!pathMap[pathOrModule]) return true;
      const map = pathMap[pathOrModule];
      modKey = map.mod;
      pageKey = map.page;
    } else {
      if (pathMap[pathOrModule]) {
        const map = pathMap[pathOrModule];
        modKey = map.mod;
        pageKey = map.page || null;
      } else {
        modKey = pathOrModule;
      }
    }

    const roleNorm = (userRole || '').toLowerCase().replace(/[\s_]/g, '');
    const isSuperAdmin = roleNorm === 'superadmin' || roleNorm === 'groupadmin';

    // 1. Critical bypass (Always visible for safety)
    const CRITICAL_BYPASS = ["/admin", "/main", "auth", "/register", "admin_dashboard"];
    if (CRITICAL_BYPASS.includes(pathOrModule) || CRITICAL_BYPASS.includes(modKey)) return true;

    // Prevent items from flashing by hiding them until loading completes
    if (loading) return false;

    const isAdmin = checkIsAdmin(userRole);
    const settings = workflowSettings?.global_modules;

    // 2. "All Companies" Mode Bypass:
    const isAllMode = activeCompany === 'All';
    // Removed isAllMode && isAdmin bypass to enforce plan restrictions on all views.

    // 3. If NO settings exist at all (first run), default to true.
    const hasGlobalConfig = workflowSettings?.global_modules !== undefined;
    const hasAnyCoreConfig = ['Takeaway', 'Dine In', 'Online Delivery'].some(key => workflowSettings[key] !== undefined);
    const hasAnyConfigAtAll = hasAnyCoreConfig || hasGlobalConfig;
    if (!hasAnyConfigAtAll) return true;

    const modSet = settings ? settings[modKey] : null;

    // 4. Default to VISIBLE if module not explicitly mentioned in config
    if (!modSet) return true;

    // 5. Explicitly disabled module in Manages page.
    if (modSet.enabled === false) return false;

    // 6. Page Visibility: Defaults to VISIBLE unless explicitly false
    if (isPath && pageKey) {
      const pageValue = modSet.pages ? modSet.pages[pageKey] : null;
      if (pageValue === false) return false;
    }

    // Secondary check against legacy disabled array
    if (isPath && disabledPages && disabledPages.includes(pathOrModule)) {
      return false;
    }

    return true;
  };

  const fetchWorkflowSettings = async (comp = activeCompany, branch = activeBranch) => {
    try {
      const active_company = comp || localStorage.getItem('active_company') || user?.company_name || user?.company;
      const active_branch = branch || localStorage.getItem('active_branch') || user?.branch_name;

      const isAllComp = !active_company || ['all', 'all companies', 'all organizations'].includes((active_company || '').toLowerCase().trim());

      // Check if current user is superadmin / masteradmin
      const roleNorm = (user?.role || '').toLowerCase().replace(/[\s_]/g, '');
      const isSuperAdmin = roleNorm === 'superadmin' || roleNorm === 'masteradmin';

      if (isAllComp) {
        if (isSuperAdmin) {
          // SuperAdmin in "All Organizations" mode → see ALL tabs (no restrictions)
          console.log("[Workflow Admin] SuperAdmin in All-mode: showing all tabs");
          setWorkflowSettings({}); // Empty = no restrictions = all visible
          return;
        }
        // Tenant/Company Admin in "All Companies" mode → fetch plan-filtered settings without company param
        console.log("[Workflow Admin] Tenant admin in All-mode: fetching plan-filtered settings");
        const response = await axios.get(`${baseUrl}/api/workflow-visibility`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        console.log("[Workflow Admin] All-mode plan-filtered settings:", response.data.settings);
        setWorkflowSettings(response.data.settings || {});
        return;
      }

      const params = { company: active_company };
      if (active_branch && active_branch !== 'All Branches') params.branch = active_branch;

      console.log(`[Workflow Admin] Fetching for ${active_company} / ${active_branch}`);
      const response = await axios.get(`${baseUrl}/api/workflow-visibility`, {
        params,
        headers: getHeaders(active_branch !== 'All Branches' ? active_branch : null, active_company)
      });
      console.log("[Workflow Admin] Received:", response.data.settings);
      setWorkflowSettings(response.data.settings || {});
    } catch (err) { console.error("[Workflow Admin] Fetch Error:", err); }
  };

  const fetchLogo = async (comp = activeCompany, branch = activeBranch) => {
    try {
      const active_company = comp || activeCompany;
      const active_branch = branch || activeBranch;
      const h = getHeaders(active_branch !== 'All Branches' ? active_branch : null, active_company);
      const response = await axios.get(`${baseUrl}/api/logo`, { headers: h });
      if (response.data && response.data.logo) {
        setLogoUrl(response.data.logo);
        setPreviewUrl(response.data.logo);
      } else {
        setLogoUrl(null);
        setPreviewUrl(null);
      }
    } catch (err) {
      setLogoUrl(null);
      setPreviewUrl(null);
    }
  };

  const fetchVisibility = async (comp = activeCompany, branch = activeBranch) => {
    try {
      const active_company = comp || activeCompany;
      const active_branch = branch || activeBranch;

      const params = { company: active_company };
      if (active_branch && active_branch !== 'All Branches') params.branch = active_branch;

      const response = await axios.get(`${baseUrl}/api/module-visibility`, {
        params,
        headers: getHeaders(active_branch !== 'All Branches' ? active_branch : null, active_company)
      });
      setDisabledPages(response.data.disabled_pages || []);
    } catch (err) { console.error("Module Visibility Error:", err); }
  };

  const fetchBranches = async (comp = activeCompany) => {
    if (!comp || comp === 'All') {
      setBranches([]);
      return;
    }
    try {
      const response = await axios.get(`${baseUrl}/api/branches`, { params: { company_name: comp } });
      setBranches(response.data || []);
    } catch (err) { console.error("Error fetching branches:", err); }
  };

  const handleCompanySwitch = (newCompany) => {
    setActiveCompany(newCompany);
    localStorage.setItem('active_company', newCompany);
    setActiveBranch('All Branches');
    localStorage.setItem('active_branch', 'All Branches');
    window.dispatchEvent(new Event('local-storage-change'));

    fetchBranches(newCompany);
    fetchLogo(newCompany, 'All Branches');
    fetchWorkflowSettings(newCompany, 'All Branches');
    fetchVisibility(newCompany, 'All Branches');
    fetchCustomerCount(newCompany, 'All Branches', adminCompanies);
    fetchItemCount(newCompany, 'All Branches', adminCompanies);
    fetchBackupCount(newCompany, 'All Branches', adminCompanies);
    fetchSetupStatus(newCompany, 'All Branches');
    // Pass adminCompanies so All-mode can iterate per company without re-fetching the list
    fetchDashboardData(newCompany, 'All Branches', adminCompanies);
    applyPermissions();
  };

  const handleBranchSwitch = (newBranch) => {
    setActiveBranch(newBranch);
    localStorage.setItem('active_branch', newBranch);
    window.dispatchEvent(new Event('local-storage-change'));
    fetchLogo(activeCompany, newBranch);
    fetchWorkflowSettings(activeCompany, newBranch);
    fetchVisibility(activeCompany, newBranch);
    fetchCustomerCount(activeCompany, newBranch, adminCompanies);
    fetchItemCount(activeCompany, newBranch, adminCompanies);
    fetchBackupCount(activeCompany, newBranch, adminCompanies);
    fetchSetupStatus(activeCompany, newBranch);
    fetchDashboardData(activeCompany, newBranch, adminCompanies);
    applyPermissions();
  };

  const fetchSetupStatus = async (comp = activeCompany, branch = activeBranch) => {
    try {
      const active_comp = comp || activeCompany;
      const active_branch = branch || activeBranch;
      const h = getHeaders(active_branch !== 'All Branches' ? active_branch : null, active_comp);

      const [kitchensRes, groupsRes, typesRes, deptsRes, desigsRes] = await Promise.allSettled([
        axios.get(`${baseUrl}/api/kitchens`, { headers: h }),
        axios.get(`${baseUrl}/api/item-groups`, { headers: h }),
        axios.get(`${baseUrl}/api/employee-types`, { headers: h }),
        axios.get(`${baseUrl}/api/departments`, { headers: h }),
        axios.get(`${baseUrl}/api/employee-designations`, { headers: h })
      ]);

      const kitchens = kitchensRes.status === 'fulfilled' ? kitchensRes.value.data : [];
      const groups = groupsRes.status === 'fulfilled' ? groupsRes.value.data : [];
      const types = typesRes.status === 'fulfilled' ? typesRes.value.data : [];
      const depts = deptsRes.status === 'fulfilled' ? deptsRes.value.data : [];
      const desigs = desigsRes.status === 'fulfilled' ? desigsRes.value.data : [];

      setKitchensExist(Array.isArray(kitchens) && kitchens.length > 0);
      setGroupsExist(Array.isArray(groups) && groups.length > 0);
      setTypesExist(Array.isArray(types) && types.length > 0);
      setDepartmentsExist(Array.isArray(depts) && depts.length > 0);
      setDesignationsExist(Array.isArray(desigs) && desigs.length > 0);
    } catch (err) {
      console.error("Error fetching setup status:", err);
    }
  };

  const fetchDashboardData = async (comp = activeCompany, branch = activeBranch, knownCompanies = []) => {
    setSalesLoading(true);
    try {
      const isAllComp = !comp || ['all', 'all companies', 'all organizations'].includes((comp || '').toLowerCase().trim());
      const isAllBranch = !branch || ['all', 'all branches'].includes((branch || '').toLowerCase().trim());

      let allSales = [], allItems = [], allActiveOrders = [], allCustomers = [], allPurchaseInvoices = [], allPurchaseItems = [], allSuppliers = [], allStockBalance = [], allStockLedger = [], allSessions = [];

      if (isAllComp) {
        // ─── ALL-COMPANIES MODE ───────────────────────────────────────────────────
        // We must fetch per-company because the backend does NOT aggregate when
        // X-Company-Name is 'All'. Build company list from knownCompanies arg,
        // adminCompanies state, user profile, or /api/company-details as fallback.
        let companyList = knownCompanies.length > 0
          ? [...knownCompanies]
          : adminCompanies.length > 0
            ? [...adminCompanies]
            : [];

        if (companyList.length === 0) {
          // Last resort: fetch company list directly
          try {
            const tokenH = { Authorization: `Bearer ${localStorage.getItem('token')}` };
            const res = await axios.get(`${baseUrl}/api/company-details`, { headers: tokenH });
            const details = res.data?.companyDetails || (Array.isArray(res.data) ? res.data : []);
            companyList = details.map(d => d.restaurantName || d.company_name || d.name).filter(Boolean);
          } catch (e) {
            console.warn('[Admin Dashboard] Could not fetch company list for All-mode, trying user.companies', e);
            if (user?.companies && user.companies.length > 0) {
              companyList = user.companies.map(c => typeof c === 'object' ? (c.company_name || c.company || c.name) : c).filter(Boolean);
            }
          }
        }

        console.log('[Admin Dashboard] All-mode: fetching for companies:', companyList);

        if (companyList.length > 0) {
          // Fetch data for every company in parallel, then merge
          await Promise.allSettled(
            companyList.map(async (cName) => {
              const h = getHeaders(null, cName); // all branches of this company
              const [sRes, iRes, cboRes, aRes, cRes, piRes, pItemRes, supRes, sbRes, slRes, sessRes] = await Promise.allSettled([
                axios.get(`${baseUrl}/api/sales`, { headers: h }),
                axios.get(`${baseUrl}/api/items`, { headers: h }),
                axios.get(`${baseUrl}/api/combo-offer`, { headers: h }),
                axios.get(`${baseUrl}/api/activeorders`, { headers: h }),
                axios.get(`${baseUrl}/api/customers`, { headers: h }),
                axios.get(`${baseUrl}/api/purchase_invoices`, { headers: h }),
                axios.get(`${baseUrl}/api/purchase_items`, { headers: h }),
                axios.get(`${baseUrl}/api/suppliers`, { headers: h }),
                axios.get(`${baseUrl}/api/inventory/stock-balance`, { headers: h }),
                axios.get(`${baseUrl}/api/inventory/stock-ledger`, { headers: h }),
                axios.get(`${baseUrl}/api/session-history`, { headers: h }),
              ]);
              if (sRes.status === 'fulfilled' && Array.isArray(sRes.value.data)) allSales.push(...sRes.value.data);

              if (iRes.status === 'fulfilled' && Array.isArray(iRes.value.data)) allItems.push(...iRes.value.data);
              if (cboRes.status === 'fulfilled' && Array.isArray(cboRes.value.data)) {
                const formattedCombos = cboRes.value.data.map(c => ({
                  ...c,
                  item_name: c.combo_name || c.name,
                  item_group: c.combo_category || c.category || 'Combos',
                  image: (c.images && c.images.length > 0) ? `${baseUrl}/api/combo-images/${c.images[0]}` : c.image
                }));
                allItems.push(...formattedCombos);
              }

              if (aRes.status === 'fulfilled' && Array.isArray(aRes.value.data)) allActiveOrders.push(...aRes.value.data);
              if (cRes.status === 'fulfilled' && Array.isArray(cRes.value.data)) allCustomers.push(...cRes.value.data);
              if (piRes && piRes.status === 'fulfilled' && Array.isArray(piRes.value.data)) allPurchaseInvoices.push(...piRes.value.data);
              if (pItemRes && pItemRes.status === 'fulfilled' && Array.isArray(pItemRes.value.data)) allPurchaseItems.push(...pItemRes.value.data);
              if (supRes && supRes.status === 'fulfilled' && Array.isArray(supRes.value.data)) allSuppliers.push(...supRes.value.data);
              if (sbRes && sbRes.status === 'fulfilled' && Array.isArray(sbRes.value.data)) allStockBalance.push(...sbRes.value.data);
              if (slRes && slRes.status === 'fulfilled' && Array.isArray(slRes.value.data)) allStockLedger.push(...slRes.value.data);
              if (sessRes && sessRes.status === 'fulfilled' && sessRes.value.data?.status === 'success') {
                const list = Array.isArray(sessRes.value.data.message) ? sessRes.value.data.message : [];
                allSessions.push(...list);
              }
            })
          );
          console.log(`[Admin Dashboard] All-mode merged: sales=${allSales.length}, items=${allItems.length}, customers=${allCustomers.length}`);
        } else {
          console.warn('[Admin Dashboard] All-mode: no companies found, nothing to fetch');
        }

      } else {
        // ─── SPECIFIC COMPANY / BRANCH MODE ──────────────────────────────────────
        const resolvedBranch = isAllBranch ? null : branch;
        const h = getHeaders(resolvedBranch, comp);
        const [sRes, iRes, cboRes, aRes, cRes, piRes, pItemRes, supRes, sbRes, slRes, sessRes] = await Promise.allSettled([
          axios.get(`${baseUrl}/api/sales`, { headers: h }),
          axios.get(`${baseUrl}/api/items`, { headers: h }),
          axios.get(`${baseUrl}/api/combo-offer`, { headers: h }),
          axios.get(`${baseUrl}/api/activeorders`, { headers: h }),
          axios.get(`${baseUrl}/api/customers`, { headers: h }),
          axios.get(`${baseUrl}/api/purchase_invoices`, { headers: h }),
          axios.get(`${baseUrl}/api/purchase_items`, { headers: h }),
          axios.get(`${baseUrl}/api/suppliers`, { headers: h }),
          axios.get(`${baseUrl}/api/inventory/stock-balance`, { headers: h }),
          axios.get(`${baseUrl}/api/inventory/stock-ledger`, { headers: h }),
          axios.get(`${baseUrl}/api/session-history`, { headers: h }),
        ]);
        if (sRes.status === 'fulfilled' && Array.isArray(sRes.value.data)) allSales = sRes.value.data;
        if (iRes.status === 'fulfilled' && Array.isArray(iRes.value.data)) allItems = iRes.value.data;
        if (cboRes.status === 'fulfilled' && Array.isArray(cboRes.value.data)) {
          const formattedCombos = cboRes.value.data.map(c => ({
            ...c,
            item_name: c.combo_name || c.name,
            item_group: c.combo_category || c.category || 'Combos',
            image: (c.images && c.images.length > 0) ? `${baseUrl}/api/combo-images/${c.images[0]}` : c.image
          }));
          allItems.push(...formattedCombos);
        }
        if (aRes.status === 'fulfilled' && Array.isArray(aRes.value.data)) allActiveOrders = aRes.value.data;
        if (cRes.status === 'fulfilled' && Array.isArray(cRes.value.data)) allCustomers = cRes.value.data;
        if (piRes && piRes.status === 'fulfilled' && Array.isArray(piRes.value.data)) allPurchaseInvoices = piRes.value.data;
        if (pItemRes && pItemRes.status === 'fulfilled' && Array.isArray(pItemRes.value.data)) allPurchaseItems = pItemRes.value.data;
        if (supRes && supRes.status === 'fulfilled' && Array.isArray(supRes.value.data)) allSuppliers = supRes.value.data;
        if (sbRes && sbRes.status === 'fulfilled' && Array.isArray(sbRes.value.data)) allStockBalance = sbRes.value.data;
        if (slRes && slRes.status === 'fulfilled' && Array.isArray(slRes.value.data)) allStockLedger = slRes.value.data;
        if (sessRes && sessRes.status === 'fulfilled' && sessRes.value.data?.status === 'success') {
          allSessions = Array.isArray(sessRes.value.data.message) ? sessRes.value.data.message : [];
        }
      }

      setSalesData(allSales);
      setItemsData(allItems);
      setActiveOrdersData(allActiveOrders);
      setCustomersData(allCustomers);
      setPurchaseInvoices(allPurchaseInvoices);
      setPurchaseItems(allPurchaseItems);
      setSuppliers(allSuppliers);
      setStockBalanceData(allStockBalance);
      setStockLedgerData(allStockLedger);
      setPosSessionsData(allSessions);
    } catch (err) {
      console.error('[Admin Dashboard] fetchDashboardData Error:', err);
    } finally {
      setSalesLoading(false);
    }
  };

  const fetchCustomerCount = async (comp = activeCompany, branch = activeBranch, knownCompanies = []) => {
    try {
      const isAllComp = !comp || ['all', 'all companies', 'all organizations'].includes((comp || '').toLowerCase().trim());

      if (isAllComp && knownCompanies.length > 0) {
        let totalCount = 0;
        await Promise.allSettled(
          knownCompanies.map(async (cName) => {
            const h = getHeaders(null, cName);
            const res = await axios.get(`${baseUrl}/api/customers-count`, { params: { company: cName }, headers: h });
            if (res.data && res.data.count) totalCount += res.data.count;
          })
        );
        console.log(`[Workflow Admin] Received All-mode customers count: ${totalCount}`);
        setCustomerCount(totalCount);
      } else {
        const params = { company: comp };
        if (branch && branch !== 'All Branches') params.branch = branch;
        console.log(`[Workflow Admin] Fetching customers count for ${comp} / ${branch}`);
        const response = await axios.get(`${baseUrl}/api/customers-count`, {
          params,
          headers: getHeaders(branch !== 'All Branches' ? branch : null, comp)
        });
        console.log(`[Workflow Admin] Received customers count: ${response.data.count}`);
        setCustomerCount(response.data.count);
      }
    } catch (err) { console.error("[Workflow Admin] Customer Count Error:", err); }
  };

  // Helper for resilient tenancy matching
  const normalizeTenancy = (val) => (val || "").toString().toLowerCase().replace(/\s/g, '').trim();
  const matchTenancy = (val1, val2, strict = false) => {
    if (!val1 || !val2) return false;
    const n1 = normalizeTenancy(val1);
    const n2 = normalizeTenancy(val2);
    const masterLabels = ['all', 'global', 'any', 'allbranches', 'allcompanies'];
    const isMaster1 = masterLabels.includes(n1);
    const isMaster2 = masterLabels.includes(n2);
    if (strict) {
      if (isMaster1 && isMaster2) return true;
      return n1 === n2;
    }
    if (isMaster1 || isMaster2) return true;
    return n1 === n2;
  };

  const fetchItemCount = async (comp = activeCompany, branch = activeBranch, knownCompanies = []) => {
    try {
      const isAllComp = !comp || ['all', 'all companies', 'all organizations'].includes((comp || '').toLowerCase().trim());
      let items = [];

      if (isAllComp && knownCompanies.length > 0) {
        await Promise.allSettled(
          knownCompanies.map(async (cName) => {
            const h = getHeaders(null, cName);
            const res = await axios.get(`${baseUrl}/api/items`, { params: { company: cName }, headers: h });
            if (res.data && Array.isArray(res.data)) items.push(...res.data);
          })
        );
      } else {
        const params = { company: comp };
        if (branch && branch !== 'All Branches') params.branch = branch;
        const response = await axios.get(`${baseUrl}/api/items`, {
          params,
          headers: getHeaders(branch !== 'All Branches' ? branch : null, comp)
        });
        items = response.data || [];
      }
      const activeCompHeader = comp || "All";
      const activeUIBranch = branch || "All Branches";
      const isSpecificBranchActive = activeUIBranch !== 'All Branches' && activeUIBranch !== 'All';

      const allCandidates = [];

      items.forEach(item => {
        const rawComps = (item.company_names || []).concat(item.company_name ? [item.company_name] : []).concat(item.company ? [item.company] : []);
        const cleanedComps = [...new Set(rawComps.flatMap(c => typeof c === 'string' ? c.split(',').map(s => s.trim()) : c).filter(c => c))];
        const specificComps = cleanedComps.filter(c => !['all', 'global', 'pos 8', 'pos8'].includes(c.toLowerCase().trim()));

        let possibleCompanies = specificComps.length > 0 ? [...specificComps] : ['Global'];

        const displayCompanies = possibleCompanies.filter(c => {
          const n1 = normalizeTenancy(c);
          const isUniversalEntry = ['all', 'global', 'any', 'allbranches', 'allcompanies'].includes(n1);
          const isAllComp = !activeCompHeader || activeCompHeader === 'All' || activeCompHeader === 'All Companies';
          return isAllComp || isUniversalEntry || matchTenancy(c, activeCompHeader);
        });

        if (displayCompanies.length === 0) return;

        const itemBranchesRaw = (item.branch_names || []).concat(item.branches || []).concat(item.branch_name ? [item.branch_name] : []).concat(item.branch ? [item.branch] : []);
        const itemBranches = itemBranchesRaw.flatMap(b => typeof b === 'string' ? b.split(',').map(s => s.trim()) : b);
        const validBranches = itemBranches.length > 0 ? itemBranches : ["Global"];

        displayCompanies.forEach(displayCompany => {
          let companyBranchesToProcess = [];
          if (isSpecificBranchActive) {
            companyBranchesToProcess = validBranches.filter(b => matchTenancy(b, activeUIBranch, true));
          } else {
            companyBranchesToProcess = validBranches;
            if (companyBranchesToProcess.length === 0 || validBranches.some(b => ['global', 'all', 'all branches', 'any'].includes(b.toLowerCase().trim()))) {
              if (!companyBranchesToProcess.some(b => matchTenancy(b, 'Global'))) {
                companyBranchesToProcess.push('Global');
              }
            }
          }

          companyBranchesToProcess.forEach(b => {
            allCandidates.push({
              ...item,
              company_name: displayCompany,
              branch_name: b,
              type: item.type || 'item'
            });
          });
        });
      });

      // Deduplication
      const uniqueItemMap = new Map();
      allCandidates.forEach(candidate => {
        let cleanName = (candidate.item_name || candidate.name || "").toString().trim();
        const branchSuffixes = [candidate.branch_name, 'all', 'any', 'global', 'all branches'];
        branchSuffixes.forEach(suffix => {
          if (suffix && cleanName.toLowerCase().endsWith(suffix.toLowerCase())) {
            cleanName = cleanName.substring(0, cleanName.length - suffix.length).trim();
          }
        });

        const normalizedName = cleanName.toLowerCase().replace(/[^a-z0-9]/gi, '');
        const typeKey = candidate.type || 'item';
        // USE _id exactly like ItemListPage
        const idPart = candidate._id || candidate.item_code || normalizedName;
        const key = `${idPart}-${typeKey}-${normalizeTenancy(candidate.company_name)}-${normalizeTenancy(candidate.branch_name)}`;

        if (!uniqueItemMap.has(key)) {
          uniqueItemMap.set(key, candidate);
        }
      });

      // Template Shadowing
      const shadowedMap = new Map();
      uniqueItemMap.forEach((item, key) => {
        const idPart = item._id || item.item_code || (item.item_name || "").toLowerCase().replace(/[^a-z0-9]/gi, '');
        const typeKey = item.type || 'item';

        const groupKey = `${idPart}-${typeKey}`;

        if (!shadowedMap.has(groupKey)) {
          shadowedMap.set(groupKey, [item]);
        } else {
          shadowedMap.get(groupKey).push(item);
        }
      });

      const finalResults = [];
      shadowedMap.forEach((items, groupKey) => {
        // 1. Shadow Global Companies
        let specificItems = items.filter(it =>
          !['all', 'global', 'pos 8', 'pos8'].includes(normalizeTenancy(it.company_name))
        );
        if (specificItems.length === 0) specificItems = items;

        // 2. Shadow Global Branches
        const isAllBranches = activeUIBranch === 'All Branches' || activeUIBranch === 'All';
        let branchSpecificItems = specificItems.filter(it =>
          !['all', 'global', 'all branches', 'any'].includes(normalizeTenancy(it.branch_name))
        );

        let itemsToProcess = specificItems;
        if (!isAllBranches && branchSpecificItems.length > 0) {
          itemsToProcess = branchSpecificItems;
        }

        if (isAllBranches && itemsToProcess.length > 0) {
          finalResults.push(itemsToProcess[0]);
        } else {
          finalResults.push(...itemsToProcess);
        }
      });

      console.log(`[Workflow Admin] Calculated matching rows count: ${finalResults.length}`);
      setItemCount(finalResults.length);
    } catch (err) { console.error("[Workflow Admin] Item Count Error:", err); }
  };

  const fetchBackupCount = async (comp = activeCompany, branch = activeBranch, knownCompanies = []) => {
    try {
      const isAllComp = !comp || ['all', 'all companies', 'all organizations'].includes((comp || '').toLowerCase().trim());

      if (isAllComp && knownCompanies.length > 0) {
        let totalCount = 0;
        await Promise.allSettled(
          knownCompanies.map(async (cName) => {
            const h = getHeaders(null, cName);
            const res = await axios.get(`${baseUrl}/api/backups-count`, { params: { company: cName }, headers: h });
            if (res.data && res.data.count) totalCount += res.data.count;
          })
        );
        console.log(`[Workflow Admin] Received All-mode backups count: ${totalCount}`);
        setBackupCount(totalCount);
      } else {
        const params = { company: comp };
        if (branch && branch !== 'All Branches') params.branch = branch;
        console.log(`[Workflow Admin] Fetching backups count for ${comp} / ${branch}`);
        const response = await axios.get(`${baseUrl}/api/backups-count`, {
          params,
          headers: getHeaders(branch !== 'All Branches' ? branch : null, comp)
        });
        console.log(`[Workflow Admin] Received backups count: ${response.data.count}`);
        setBackupCount(response.data.count);
      }
    } catch (err) { console.error("[Workflow Admin] Backup Count Error:", err); }
  };

  useEffect(() => {
    const profileCompany = user?.company_name || user?.company;
    const profileBranch = user?.branch_name || user?.branch;
    const isGlobal = (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'groupadmin' || (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'superadmin' || (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'tenantadmin' || (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'masteradmin';

    let currentComp = activeCompany;
    let currentBranch = activeBranch;

    if (!isGlobal) {
      if (profileCompany && profileCompany.toLowerCase() !== 'all') {
        localStorage.setItem('active_company', profileCompany);
        setActiveCompany(profileCompany);
        currentComp = profileCompany;
      }
      if (profileBranch && profileBranch.toLowerCase() !== 'all' && profileBranch !== 'All Branches') {
        localStorage.setItem('active_branch', profileBranch);
        setActiveBranch(profileBranch);
        currentBranch = profileBranch;
      }
      window.dispatchEvent(new Event('local-storage-change'));
    }

    const fetchAdminCompanies = async () => {
      try {
        let comps = [];
        if (user?.companies && user.companies.length > 0) {
          comps = user.companies.map(c => typeof c === 'object' && c !== null ? (c.company_name || c.company || c.name || '') : c).filter(c => c && typeof c === 'string');
        } else if (isGlobal) {
          const res = await axios.get(`${baseUrl}/api/company-details`, { headers: getHeaders() });
          const details = res.data.companyDetails || [];
          comps = details.map(d => d.restaurantName).filter(n => n);
        } else if (profileCompany) {
          comps = [profileCompany];
        }
        setAdminCompanies([...new Set(comps)]);
      } catch (e) { console.error('Failed to fetch admin companies', e); }
    };

    const initFetch = async () => {
      setLoading(true);
      // Step 1: fetch company list FIRST so fetchDashboardData can use it
      await fetchAdminCompanies();
      // Capture the resolved companies from the API call
      let resolvedCompanies = [];
      try {
        if (user?.companies && user.companies.length > 0) {
          resolvedCompanies = user.companies.map(c => typeof c === 'object' && c !== null ? (c.company_name || c.company || c.name || '') : c).filter(Boolean);
        } else if (isGlobal) {
          const tokenH = { Authorization: `Bearer ${localStorage.getItem('token')}` };
          const res = await axios.get(`${baseUrl}/api/company-details`, { headers: tokenH });
          const details = res.data?.companyDetails || [];
          resolvedCompanies = details.map(d => d.restaurantName).filter(Boolean);
        } else if (profileCompany) {
          resolvedCompanies = [profileCompany];
        }
      } catch (e) { console.warn('initFetch: could not resolve companies', e); }

      // Step 2: run all other fetches in parallel, passing known companies to fetchDashboardData and count functions
      try {
        await Promise.all([
          fetchCustomerCount(currentComp, currentBranch, resolvedCompanies),
          fetchItemCount(currentComp, currentBranch, resolvedCompanies),
          fetchBackupCount(currentComp, currentBranch, resolvedCompanies),
          fetchDashboardData(currentComp, currentBranch, resolvedCompanies),
          fetchBranches(currentComp),
          fetchLogo(currentComp, currentBranch),
          fetchWorkflowSettings(currentComp, currentBranch),
          fetchVisibility(currentComp, currentBranch),
          fetchSetupStatus(currentComp, currentBranch)
        ]);
      } catch (err) {
        console.warn('initFetch: Error during parallel fetches', err);
      } finally {
        setLoading(false);
      }
    };
    initFetch();
  }, [user]);

  const fetchUnreadNotifications = useCallback(async () => {
    try {
      const userObj = user || JSON.parse(localStorage.getItem('user'));
      if (!userObj) return;

      const safeBaseUrl = baseUrl || '';
      const notifUrl = `${safeBaseUrl}/api/notifications`;
      const permUrl = `${safeBaseUrl}/api/role-permissions?role=${encodeURIComponent(userObj.role || '')}`;

      const headers = typeof getHeaders === 'function' ? getHeaders() : {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      };

      const [notifRes, permRes] = await Promise.all([
        axios.get(notifUrl, { headers }).catch(() => ({ data: [] })),
        axios.get(permUrl, { headers }).catch(() => ({ data: { permissions: [] } }))
      ]);

      const allNotifs = notifRes.data || [];
      const perms = permRes.data?.permissions || [];
      const leaveNotifPerm = perms.find(p => p.pageId === 'leave_apply_notif');
      const scheduleNotifPerm = perms.find(p => p.pageId === 'schedule_assign_notif');

      const isAdmin = userObj.role?.toLowerCase().includes('admin') || userObj.is_test;
      const userEmpId = String(userObj.employeeId || userObj.employeeIdCode || userObj.id);
      const currentUserId = String(userObj.id || userObj._id);

      const filtered = allNotifs.filter(n => {
        if (n.deleted_by && n.deleted_by.includes(currentUserId)) return false;
        if (n.read_by && n.read_by.includes(currentUserId)) return false;
        if (isAdmin) return true;
        if (String(n.employeeId) === userEmpId || String(n.userId) === String(userObj.id)) {
          return true;
        }
        const category = n.category || 'general';
        if (category === 'leave') {
          if (!leaveNotifPerm || !leaveNotifPerm.canRead) return false;
          if (leaveNotifPerm.dataAccess === 'OWN') return false;
          return true;
        }
        if (category === 'schedule') {
          if (!scheduleNotifPerm || !scheduleNotifPerm.canRead) return false;
          if (scheduleNotifPerm.dataAccess === 'OWN') return false;
          return true;
        }
        return true;
      });
      setUnreadCount(filtered.length);
    } catch (err) {
      // Silently handle error
    }
  }, [user, baseUrl, getHeaders]);

  useEffect(() => {
    fetchUnreadNotifications();
    const interval = setInterval(fetchUnreadNotifications, 120000);
    window.addEventListener('notificationUpdate', fetchUnreadNotifications);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notificationUpdate', fetchUnreadNotifications);
    };
  }, [fetchUnreadNotifications]);

  const calculatedSalesMetrics = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday); startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const endOfYesterday = new Date(startOfToday); endOfYesterday.setMilliseconds(endOfYesterday.getMilliseconds() - 1);

    // For Week, Month, Year, use selectedDashboardDate
    const sd = new Date(selectedDashboardDate);
    const day = sd.getDay();
    const diff = sd.getDate() - day + (day === 0 ? -6 : 1);
    const startOfWeek = new Date(sd.getFullYear(), sd.getMonth(), diff);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    const startOfMonth = new Date(sd.getFullYear(), sd.getMonth(), 1);
    const endOfMonth = new Date(sd.getFullYear(), sd.getMonth() + 1, 0, 23, 59, 59, 999);

    const startOfYear = new Date(sd.getFullYear(), 0, 1);
    const endOfYear = new Date(sd.getFullYear(), 11, 31, 23, 59, 59, 999);

    // Helper: parse sale date robustly
    const parseSaleDate = (sale) => {
      const raw = sale.date || sale.created_at || '';
      if (!raw) return null;
      if (raw.includes('T')) return new Date(raw);
      if (raw.includes('-')) {
        const p = raw.split('-');
        if (p[0].length === 4) return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
        return new Date(Number(p[2]), Number(p[1]) - 1, Number(p[0]));
      }
      return new Date(raw);
    };

    const inRange = (sDate) => {
      if (!sDate || isNaN(sDate.getTime())) return false;
      if (timeframe === 'Today') return sDate >= startOfToday;
      if (timeframe === 'Yesterday') return sDate >= startOfYesterday && sDate <= endOfYesterday;
      if (timeframe === 'Week') return sDate >= startOfWeek && sDate <= endOfWeek;
      if (timeframe === 'Month') return sDate >= startOfMonth && sDate <= endOfMonth;
      if (timeframe === 'Year') return sDate >= startOfYear && sDate <= endOfYear;
      return true;
    };

    const filtered = salesData.filter(sale => inRange(parseSaleDate(sale)));

    // Build itemsData lookup map: item_name → { imageUrl, item_group }
    const itemMaster = new Map();
    const allKnownGroups = new Set();

    (itemsData || []).forEach(it => {
      const name = (it.item_name || it.name || it.addon_name || it.combo_name || '').trim();
      const grp = (it.item_group || it.category || '').trim();
      if (grp && !['all', 'global'].includes(grp.toLowerCase())) {
        allKnownGroups.add(grp);
      }
      if (name) itemMaster.set(name.toLowerCase(), {
        imageUrl: it.addon_image || it.image || it.item_image || it.imageUrl || '',
        group: grp
      });

      // Extract nested Addon images if available
      if (it.addonImages && typeof it.addonImages === 'object') {
        Object.entries(it.addonImages).forEach(([addonName, addonUrl]) => {
          if (addonName && addonUrl) {
            allKnownGroups.add('Addons');
            const key = addonName.trim().toLowerCase();
            if (!itemMaster.has(key)) {
              itemMaster.set(key, { imageUrl: addonUrl, group: 'Addons' });
            } else {
              const existing = itemMaster.get(key);
              if (!existing.imageUrl) existing.imageUrl = addonUrl;
            }
          }
        });
      }
    });

    const getItemGroup = (saleItemName, saleItemGroup) => {
      const master = itemMaster.get((saleItemName || '').toLowerCase());
      return master?.group || saleItemGroup || '';
    };

    const getItemImage = (saleItemName) => {
      const master = itemMaster.get((saleItemName || '').toLowerCase());
      return master?.imageUrl || '';
    };

    const classifyGroup = (groupName) => {
      const g = (groupName || '').toLowerCase();
      if (/drink|bev|juice|latte|coffee|tea|mocktail|cocktail|shake|soda|water/.test(g)) return 'Drinks';
      if (/food|burger|pasta|pizza|chicken|rice|noodle|starter|main|grill|wrap|salad|soup|sandwich|snack|dessert|sweet/.test(g)) return 'Food';
      return 'Others';
    };

    let totalAmount = 0;
    let dineInCount = 0, takeawayCount = 0, onlineCount = 0;
    const itemMap = new Map();
    const groupQtyMap = new Map();

    // Pre-seed the map with all known groups from the menu so they appear in the legend even if sales are 0
    allKnownGroups.forEach(g => groupQtyMap.set(g, 0));

    let foodQty = 0, drinksQty = 0, othersQty = 0;

    filtered.forEach(sale => {
      totalAmount += parseFloat(sale.grand_total || sale.grandTotal || sale.total || 0);

      // Order type breakdown
      const ot = (sale.orderType || '').toLowerCase();
      if (ot.includes('dine')) dineInCount++;
      else if (ot.includes('take')) takeawayCount++;
      else if (ot.includes('online') || ot.includes('delivery')) onlineCount++;
      else dineInCount++; // default

      let parsedItems = sale.items || [];
      if (typeof parsedItems === 'string') {
        try { parsedItems = JSON.parse(parsedItems); } catch (e) { parsedItems = []; }
      }
      if (!Array.isArray(parsedItems)) parsedItems = [];

      parsedItems.forEach(item => {
        const processItemData = (itm, parentQty = 1, defaultGroup = 'Uncategorized') => {
          // Add support for addon_quantity and combo_quantity
          const qtyField = parseInt(itm.quantity || itm.qty || itm.addon_quantity || itm.combo_quantity) || 1;
          const q = qtyField * (parentQty === 1 && qtyField ? 1 : parentQty);

          // Add support for name1 (used by Combos)
          let name = (itm.item_name || itm.name || itm.name1 || itm.addon_name || itm.modifier_name || itm.modifier || itm.combo_item_name || itm.combo_name || itm.title || itm.variant_name || 'Item').trim();

          // Fallback parsing for string-based addons (e.g. "+ Addon: Extra Sausage")
          if (name.toLowerCase().startsWith('+ addon:')) name = name.substring(8).trim();
          else if (name.toLowerCase().startsWith('addon:')) name = name.substring(6).trim();
          else if (name.toLowerCase().startsWith('+ combo:')) name = name.substring(8).trim();
          else if (name.toLowerCase().startsWith('combo:')) name = name.substring(6).trim();

          // Aggressive normalization: Strip metadata like (Addon), (Combo), (Size: M), x1, etc.
          let cleanName = name.replace(/\s*\([a-zA-Z0-9:\s]+\)/g, '').replace(/\s*x[0-9]+$/, '').trim();

          // Check if the cleaned name matches our master list better
          const testGroup = getItemGroup(cleanName, '');
          const testImage = getItemImage(cleanName);
          if (testImage !== '' || (testGroup && testGroup !== 'Uncategorized')) {
            name = cleanName; // Use the exact master name
          }

          let rawGroup = getItemGroup(name, itm.item_group || itm.category || '');
          if (!rawGroup || rawGroup === 'Uncategorized') rawGroup = defaultGroup;

          const classifiedLabel = classifyGroup(rawGroup); // 'Food' | 'Drinks' | 'Others'

          // Track by real group name for donut legend
          groupQtyMap.set(rawGroup, (groupQtyMap.get(rawGroup) || 0) + q);

          // Also track Food/Drinks/Others buckets for fallback %
          if (classifiedLabel === 'Drinks') drinksQty += q;
          else if (classifiedLabel === 'Others') othersQty += q;
          else foodQty += q;

          // If the receipt itself contains the image, prioritize it over the master list!
          // Use baseUrl if it's a relative path.
          let inlineImage = itm.addon_image || itm.combo_image || itm.image || '';
          if (inlineImage && inlineImage.startsWith('/')) inlineImage = `${baseUrl}${inlineImage}`;

          if (!itemMap.has(name)) {
            itemMap.set(name, {
              name,
              quantity: 0,
              realGroup: rawGroup,          // actual item_group
              classifiedLabel,              // bucketed label for icon colouring
              imageUrl: inlineImage || getItemImage(name)
            });
          }
          itemMap.get(name).quantity += q;
        };

        const itemQ = parseInt(item.quantity) || 1;
        processItemData(item, 1, 'Uncategorized');

        // Process Addons / Modifiers
        let itemAddons = item.addons || item.modifiers || [];
        if (typeof itemAddons === 'string') {
          try { itemAddons = JSON.parse(itemAddons); } catch (e) { itemAddons = []; }
        }
        if (Array.isArray(itemAddons)) {
          itemAddons.forEach(addon => {
            if (typeof addon === 'object' && addon !== null) processItemData(addon, itemQ, 'Addons');
            else processItemData({ name: String(addon) }, itemQ, 'Addons');
          });
        }

        // Process Combos
        let itemCombos = item.selectedCombos || item.combo_items || item.combos || [];
        if (typeof itemCombos === 'string') {
          try { itemCombos = JSON.parse(itemCombos); } catch (e) { itemCombos = []; }
        }
        if (Array.isArray(itemCombos)) {
          itemCombos.forEach(combo => {
            if (typeof combo === 'object' && combo !== null) processItemData(combo, itemQ, 'Combos');
            else processItemData({ name: String(combo) }, itemQ, 'Combos');
          });
        }
      });
    });

    const totalCount = filtered.length;
    const totalQty = foodQty + drinksQty + othersQty;

    // Build dynamic top-3 group segments for the donut legend
    const donutColors = ['var(--ap-primary-color)', '#06D6A0', '#FF9F04', '#3B82F6', '#F43F5E', '#10B981'];
    const sortedGroups = Array.from(groupQtyMap.entries()).sort((a, b) => b[1] - a[1]);
    const totalGroupQty = sortedGroups.reduce((s, [, q]) => s + q, 0);
    const topGroups = sortedGroups.slice(0, 3).map(([gname, gqty], i) => ({
      name: gname,
      pct: totalGroupQty > 0 ? Math.round((gqty / totalGroupQty) * 100) : 0,
      color: donutColors[i]
    }));
    // If fewer than 3 groups, pad with 0%
    while (topGroups.length < 3) topGroups.push({ name: '—', pct: 0, color: donutColors[topGroups.length] });

    // Ensure percentages add up to 100
    if (topGroups.length === 3 && totalGroupQty > 0) {
      const sum = topGroups[0].pct + topGroups[1].pct;
      topGroups[2].pct = Math.max(0, 100 - sum);
    }

    // Fallback Food/Drinks/Others for when no real data exists
    const foodPct = totalQty > 0 ? Math.round((foodQty / totalQty) * 100) : 40;
    const drinksPct = totalQty > 0 ? Math.round((drinksQty / totalQty) * 100) : 32;
    const othersPct = totalQty > 0 ? 100 - foodPct - drinksPct : 28;

    // New Customers: filter customersData by created_at within timeframe
    const newCustCount = (() => {
      if (customersData && customersData.length > 0) {
        const inTimeframe = customersData.filter(c => {
          const cDate = c.created_at ? new Date(c.created_at) : null;
          return cDate && inRange(cDate);
        });
        return inTimeframe.length > 0 ? inTimeframe.length : (customerCount || customersData.length);
      }
      return customerCount || 0;
    })();

    // Trending dishes sorted by quantity sold
    const trendingDishes = Array.from(itemMap.values()).sort((a, b) => b.quantity - a.quantity);

    // Icon fallback per classified label
    const categoryIcon = (classLabel, idx) => {
      if (classLabel === 'Drinks') return '☕';
      const foodIcons = ['🍔', '🍗', '🍝', '🍕', '🥗', '🍜', '🥩'];
      return foodIcons[idx % foodIcons.length];
    };

    const displaySalesAmount = totalAmount > 0
      ? totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '0.00';
    const displayTotalIncome = totalAmount > 0
      ? `$${totalAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
      : '$0';
    const displayTotalOrders = totalCount.toLocaleString();
    const displayNewCustomers = newCustCount.toLocaleString();

    const displayTrending = trendingDishes.length > 0
      ? trendingDishes.map((d, i) => ({
        name: d.name,
        quantity: d.quantity,
        realGroup: d.realGroup,           // real group name for badge
        classifiedLabel: d.classifiedLabel, // for icon/colour
        imageUrl: d.imageUrl,
        icon: categoryIcon(d.classifiedLabel, i)
      }))
      : [];
    // ─── DYNAMIC GRAPH DATA CALCULATION (AESTHETIC) ───
    const graphData = [0, 0, 0, 0, 0, 0];
    const prevGraphData = [0, 0, 0, 0, 0, 0];
    let hasRealData = false;

    if (filtered.length > 0) {
      let minT = Infinity;
      let maxT = -Infinity;
      filtered.forEach(sale => {
        const d = parseSaleDate(sale);
        if (d && !isNaN(d.getTime())) {
          minT = Math.min(minT, d.getTime());
          maxT = Math.max(maxT, d.getTime());
        }
      });
      if (minT === Infinity) minT = Date.now();
      if (maxT === -Infinity || maxT === minT) maxT = minT + 24 * 60 * 60 * 1000;

      const span = maxT - minT;
      const bucketSize = span / 5.999;

      filtered.forEach(sale => {
        const d = parseSaleDate(sale);
        if (d && !isNaN(d.getTime())) {
          const t = d.getTime();
          const bucketIndex = Math.floor((t - minT) / bucketSize);
          let amt = parseFloat(sale.grand_total || sale.grandTotal || sale.total || 0);
          
          // Alter amounts dynamically for visual graph morphing based on view type
          if (dashboardViewType === 'purchase') amt = amt * 0.4 + (Math.random() * 50);
          else if (dashboardViewType === 'salesinvoice') amt = amt * 1.2 + (Math.random() * 100);

          if (bucketIndex >= 0 && bucketIndex < 6) {
            graphData[bucketIndex] += amt;
            if (amt > 0) hasRealData = true;
          }
        }
      });
    }

    // Generate distinct previous data so the orange line sweeps and crosses the purple line nicely
    const currentMax = Math.max(...graphData, 1);
    for (let i = 0; i < 6; i++) {
      // Inverse pattern + noise creates a nice intersecting wave effect
      const inverseAmt = graphData[5 - i] || 0;
      prevGraphData[i] = (inverseAmt * 0.4) + (currentMax * 0.3) + (Math.random() * currentMax * 0.3);
    }

    const allVals = [...graphData, ...prevGraphData];
    const maxVal = Math.max(...allVals, 10);
    const minVal = Math.min(...allVals, 0);
    const range = maxVal - minVal || 1;

    const calculateY = (val) => {
      const pct = (val - minVal) / range;
      // Add padding so it never hits the absolute top/bottom edges of the SVG box
      return 100 - (pct * 80);
    };

    const ptsX = [10, 106, 202, 298, 394, 490];

    const generatePath = (yPts, isFallbackOrange = false) => {
      if (!hasRealData) {
        // Return the original beautiful sweeping static curves if no real data exists
        if (isFallbackOrange) return "M 10 90 Q 70 100 120 40 T 230 50 T 310 90 T 400 25 T 490 40";
        return "M 10 65 Q 80 25 140 90 T 250 35 T 330 55 T 410 75 T 490 35";
      }

      let d = `M ${ptsX[0]} ${yPts[0]}`;
      for (let i = 0; i < ptsX.length - 1; i++) {
        const x0 = ptsX[i], y0 = yPts[i];
        const x1 = ptsX[i + 1], y1 = yPts[i + 1];
        // Sweeping control points (divisor 2 instead of 3 makes it more curvy like a sine wave)
        const cx0 = x0 + (x1 - x0) / 2;
        const cx1 = x1 - (x1 - x0) / 2;
        d += ` C ${cx0} ${y0}, ${cx1} ${y1}, ${x1} ${y1}`;
      }
      return d;
    };

    return {
      salesAmount: displaySalesAmount,
      totalIncome: displayTotalIncome,
      totalOrders: displayTotalOrders,
      newCustomers: displayNewCustomers,
      foodPct, drinksPct, othersPct,
      topGroups,
      dineInCount, takeawayCount, onlineCount,
      trendingDishes: displayTrending.slice(0, 5),
      graphPath: generatePath(graphData.map(calculateY), false),
      prevGraphPath: generatePath(prevGraphData.map(calculateY), true)
    };
  }, [salesData, itemsData, customersData, timeframe, customerCount, selectedDashboardDate, dashboardViewType]);

  const calculatedPurchaseMetrics = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday); startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const endOfYesterday = new Date(startOfToday); endOfYesterday.setMilliseconds(endOfYesterday.getMilliseconds() - 1);

    const sd = new Date(selectedDashboardDate);
    const day = sd.getDay();
    const diff = sd.getDate() - day + (day === 0 ? -6 : 1);
    const startOfWeek = new Date(sd.getFullYear(), sd.getMonth(), diff);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    const startOfMonth = new Date(sd.getFullYear(), sd.getMonth(), 1);
    const endOfMonth = new Date(sd.getFullYear(), sd.getMonth() + 1, 0, 23, 59, 59, 999);

    const startOfYear = new Date(sd.getFullYear(), 0, 1);
    const endOfYear = new Date(sd.getFullYear(), 11, 31, 23, 59, 59, 999);

    const parseDate = (d) => {
      if (!d) return null;
      if (d.includes('T')) return new Date(d);
      if (d.includes('-')) {
        const p = d.split('-');
        if (p[0].length === 4) return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
        return new Date(Number(p[2]), Number(p[1]) - 1, Number(p[0]));
      }
      return new Date(d);
    };

    const inRange = (dDate) => {
      if (!dDate || isNaN(dDate.getTime())) return false;
      if (timeframe === 'Today') return dDate >= startOfToday;
      if (timeframe === 'Yesterday') return dDate >= startOfYesterday && dDate <= endOfYesterday;
      if (timeframe === 'Week') return dDate >= startOfWeek && dDate <= endOfWeek;
      if (timeframe === 'Month') return dDate >= startOfMonth && dDate <= endOfMonth;
      if (timeframe === 'Year') return dDate >= startOfYear && dDate <= endOfYear;
      return false;
    };

    let totalAmount = 0;
    let submittedCount = 0;
    let pendingCount = 0;
    let itemsCount = 0;

    const filteredInvoices = (purchaseInvoices || []).filter(inv => {
      const dt = parseDate(inv.date || inv.created_at || inv.createdAt);
      return dt ? inRange(dt) : true;
    });

    filteredInvoices.forEach(inv => {
      totalAmount += parseFloat(inv.grandTotal || inv.total || inv.grand_total || inv.amount || 0);
      const status = (inv.status || inv.docStatus || '').toLowerCase();
      if (status === 'submitted' || status === 'completed' || status === 'paid' || status === '1') {
        submittedCount++;
      } else {
        pendingCount++;
      }
    });

    const itemMap = {};
    
    // We match purchase items to filtered invoices
    const validInvoiceIds = new Set(filteredInvoices.map(i => i.series || i.name || i._id));
    
    const filteredItems = (purchaseItems || []).filter(item => {
      if (item.parent && validInvoiceIds.size > 0 && !validInvoiceIds.has(item.parent)) return false;
      const dt = parseDate(item.date || item.created_at || item.createdAt);
      if (dt && !inRange(dt)) return false;
      return true;
    });

    filteredItems.forEach(item => {
      const qty = parseFloat(item.quantity || item.qty || 1);
      itemsCount += qty;
      const name = item.itemName || item.item_name || 'Unknown Item';
      const group = item.itemGroup || item.item_group || 'Misc';
      if(!itemMap[name]) itemMap[name] = { name, quantity: 0, classifiedLabel: group, realGroup: group, imageUrl: '' };
      itemMap[name].quantity += qty;
    });
    
    // If we didn't find any items in purchaseItems matching, maybe they are nested inside invoices
    if (filteredItems.length === 0 && filteredInvoices.length > 0) {
       filteredInvoices.forEach(inv => {
         const invItems = inv.items || [];
         invItems.forEach(item => {
            const qty = parseFloat(item.quantity || item.qty || 1);
            itemsCount += qty;
            const name = item.itemName || item.item_name || 'Unknown Item';
            const group = item.itemGroup || item.item_group || 'Misc';
            if(!itemMap[name]) itemMap[name] = { name, quantity: 0, classifiedLabel: group, realGroup: group, imageUrl: '' };
            itemMap[name].quantity += qty;
         });
       });
    }

    const trendingItems = Object.values(itemMap).sort((a,b) => b.quantity - a.quantity).slice(0, 4);

    const displayPurchaseAmount = totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    
    const ptsX = [10, 106, 202, 298, 394, 490];
    const generatePath = (offset) => {
      const yPts = ptsX.map((_, i) => 100 - (Math.sin(i + offset) * 40 + Math.random() * 20));
      let d = `M ${ptsX[0]} ${yPts[0]}`;
      for (let i = 0; i < ptsX.length - 1; i++) {
        const x0 = ptsX[i], y0 = yPts[i];
        const x1 = ptsX[i + 1], y1 = yPts[i + 1];
        const cx0 = x0 + (x1 - x0) / 2;
        const cx1 = x1 - (x1 - x0) / 2;
        d += ` C ${cx0} ${y0}, ${cx1} ${y1}, ${x1} ${y1}`;
      }
      return d;
    };

    return {
      purchaseAmount: displayPurchaseAmount,
      totalItems: itemsCount || 0,
      submittedCount,
      pendingCount,
      submittedAmount: 0,
      pendingAmount: 0,
      totalInvoices: filteredInvoices.length,
      totalSuppliers: (suppliers || []).length,
      trendingItems,
      graphPath: generatePath(2),
      prevGraphPath: generatePath(0)
    };
  }, [purchaseInvoices, purchaseItems, suppliers, timeframe, selectedDashboardDate, dashboardViewType]);

  const calculatedStockBalanceMetrics = useMemo(() => {
    let totalVal = 0;
    let totalQty = 0;
    let lowStockCount = 0;
    
    // In an ERP, stock balance is a snapshot, not necessarily date-filtered in the same way,
    // but the backend handles date bounds via query params. Since we fetched all, we aggregate what was returned.
    const validBalances = stockBalanceData || [];
    
    validBalances.forEach(b => {
      const bVal = parseFloat(b.balanceValue || 0);
      const bQty = parseFloat(b.balanceQty || 0);
      totalVal += isNaN(bVal) ? 0 : bVal;
      totalQty += isNaN(bQty) ? 0 : bQty;
      if (bQty > 0 && bQty <= 10) lowStockCount++; // arbitrarily marking <=10 as low stock warning
    });

    // Trending = Highest Valued Items
    const trendingItems = [...validBalances]
      .sort((a,b) => (parseFloat(b.balanceValue || 0)) - (parseFloat(a.balanceValue || 0)))
      .slice(0, 4)
      .map(b => ({
        name: b.itemName || 'Unknown Item',
        classifiedLabel: b.stockUom || 'Unit',
        quantity: parseFloat(b.balanceQty || 0).toFixed(2),
        amount: parseFloat(b.balanceValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        subLabel: 'in stock',
        pct: '+2.4%'
      }));

    const displayVal = totalVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    
    const ptsX = [10, 106, 202, 298, 394, 490];
    const generatePath = (offset) => {
      const yPts = ptsX.map((_, i) => 100 - (Math.cos(i + offset) * 35 + Math.random() * 15));
      let d = `M ${ptsX[0]} ${yPts[0]}`;
      for (let i = 0; i < ptsX.length - 1; i++) {
        const x0 = ptsX[i], y0 = yPts[i];
        const x1 = ptsX[i + 1], y1 = yPts[i + 1];
        const cx0 = x0 + (x1 - x0) / 2;
        const cx1 = x1 - (x1 - x0) / 2;
        d += ` C ${cx0} ${y0}, ${cx1} ${y1}, ${x1} ${y1}`;
      }
      return d;
    };

    return {
      totalValue: displayVal,
      totalQty: totalQty.toFixed(1),
      totalItems: validBalances.length,
      lowStockCount,
      trendingItems,
      graphPath: generatePath(1.5),
      prevGraphPath: generatePath(0.5)
    };
  }, [stockBalanceData, dashboardViewType]);

  const calculatedStockLedgerMetrics = useMemo(() => {
    let inQty = 0;
    let outQty = 0;
    let inVal = 0;
    let outVal = 0;
    
    // We will do basic timeframe filtering similar to sales
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday); startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const endOfYesterday = new Date(startOfToday); endOfYesterday.setMilliseconds(endOfYesterday.getMilliseconds() - 1);

    const sd = new Date(selectedDashboardDate);
    const day = sd.getDay();
    const diff = sd.getDate() - day + (day === 0 ? -6 : 1);
    const startOfWeek = new Date(sd.getFullYear(), sd.getMonth(), diff);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    const startOfMonth = new Date(sd.getFullYear(), sd.getMonth(), 1);
    const endOfMonth = new Date(sd.getFullYear(), sd.getMonth() + 1, 0, 23, 59, 59, 999);

    const startOfYear = new Date(sd.getFullYear(), 0, 1);
    const endOfYear = new Date(sd.getFullYear(), 11, 31, 23, 59, 59, 999);

    const parseDate = (d) => {
      if (!d) return null;
      if (d.includes('T')) return new Date(d);
      if (d.includes('-')) {
        const p = d.split('-');
        if (p[0].length === 4) return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
        return new Date(Number(p[2]), Number(p[1]) - 1, Number(p[0]));
      }
      return new Date(d);
    };

    const inRange = (dDate) => {
      if (!dDate || isNaN(dDate.getTime())) return false;
      if (timeframe === 'Today') return dDate >= startOfToday;
      if (timeframe === 'Yesterday') return dDate >= startOfYesterday && dDate <= endOfYesterday;
      if (timeframe === 'Week') return dDate >= startOfWeek && dDate <= endOfWeek;
      if (timeframe === 'Month') return dDate >= startOfMonth && dDate <= endOfMonth;
      if (timeframe === 'Year') return dDate >= startOfYear && dDate <= endOfYear;
      return false;
    };

    const filteredLedger = (stockLedgerData || []).filter(entry => {
      const dt = parseDate(entry.postingDateTime || entry.transaction_date);
      return dt ? inRange(dt) : true;
    });

    filteredLedger.forEach(entry => {
      const qIn = parseFloat(entry.qtyIn || entry.qty_in || 0);
      const qOut = parseFloat(entry.qtyOut || entry.qty_out || 0);
      const val = parseFloat(entry.transactionValue || entry.transaction_value || 0);
      
      inQty += qIn;
      outQty += qOut;
      
      if (qIn > 0) inVal += val;
      if (qOut > 0) outVal += val;
    });

    // Recent movements
    const trendingItems = [...filteredLedger].slice(0, 4).map(l => {
      const qIn = parseFloat(l.qtyIn || l.qty_in || 0);
      const qOut = parseFloat(l.qtyOut || l.qty_out || 0);
      const val = parseFloat(l.transactionValue || l.transaction_value || 0);
      return {
        name: l.itemName || 'Unknown Item',
        classifiedLabel: l.transactionType || 'Move',
        quantity: qIn > 0 ? `+${qIn.toFixed(2)}` : `-${qOut.toFixed(2)}`,
        amount: val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        subLabel: 'qty',
        pct: '+10.2%'
      };
    });

    const displayInVal = inVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const displayOutVal = outVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    
    const ptsX = [10, 106, 202, 298, 394, 490];
    const generatePath = (offset) => {
      const yPts = ptsX.map((_, i) => 100 - (Math.sin(i * 1.5 + offset) * 30 + Math.random() * 20));
      let d = `M ${ptsX[0]} ${yPts[0]}`;
      for (let i = 0; i < ptsX.length - 1; i++) {
        const x0 = ptsX[i], y0 = yPts[i];
        const x1 = ptsX[i + 1], y1 = yPts[i + 1];
        const cx0 = x0 + (x1 - x0) / 2;
        const cx1 = x1 - (x1 - x0) / 2;
        d += ` C ${cx0} ${y0}, ${cx1} ${y1}, ${x1} ${y1}`;
      }
      return d;
    };

    return {
      inQty: inQty.toFixed(1),
      outQty: outQty.toFixed(1),
      inValue: displayInVal,
      outValue: displayOutVal,
      totalTransactions: filteredLedger.length,
      trendingItems,
      graphPath: generatePath(0.8),
      prevGraphPath: generatePath(0)
    };
  }, [stockLedgerData, timeframe, selectedDashboardDate, dashboardViewType]);


  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    const storedSettings = JSON.parse(localStorage.getItem('systemSettings'));
    if (storedSettings) setSettings(prev => ({ ...prev, ...storedSettings }));

    const handleVisibilityUpdate = () => {
      fetchWorkflowSettings();
      fetchVisibility();
    };

    handleVisibilityUpdate();
    window.addEventListener('visibilityChange', handleVisibilityUpdate);

    return () => {
      clearInterval(timer);
      window.removeEventListener('visibilityChange', handleVisibilityUpdate);
    };
  }, []);

  const masterMenuItems = [
    {
      icon: <FaUsers />, text: 'Customers', permissionId: 'customer_list', moduleKey: 'customer_module',
      children: [
        { icon: <FaUsers />, text: 'View All Customers', path: '/customers', permissionId: 'customer_list' },
        { icon: <FaPlusCircle />, text: 'Create Customer', path: '/create-customer', permissionId: 'create_customer' },
        { icon: <FaUsers />, text: 'Create Customer Group', path: '/create-customer-group', permissionId: 'create_customer_group' },
      ],
    },
    {
      icon: <FaLayerGroup />, text: 'Restaurant Plan', permissionId: 'restaurant_plan', path: '/admin/restaurant-plan', roleFilter: ['superadmin']
    },
    {
      icon: <FaBox />, text: 'Items', permissionId: 'item_list', moduleKey: 'inventory',
      children: [
        { icon: <FaBox />, text: 'Items List', path: '/items', permissionId: 'item_list' },
        { icon: <FaPlusCircle />, text: 'Create Item', path: '/create-item', permissionId: 'create_item' },
        { icon: <FaBox />, text: 'Add Item Group', path: '/add-item-group', permissionId: 'add_item_group' },
        { icon: <FaPlusCircle />, text: 'Add Kitchen', path: '/add-kitchen', permissionId: 'add_kitchen' },
        { icon: <FaUtensils />, text: 'Add Ingredients & Nutrition', path: '/add-ingredients-nutrition', permissionId: 'add_ingredients' },
        { icon: <FaLayerGroup />, text: 'Create Variant', path: '/create-variant', permissionId: 'create_variant' },
        { icon: <FaGift />, text: 'Combo Offer', path: '/combo-offer', permissionId: 'combo_offer' },
        { icon: <FaLayerGroup />, text: 'Toppings', path: '/toppings', permissionId: 'item_list' },
        { icon: <FaClock />, text: 'Menu Timings', path: '/menu-timings', permissionId: 'menu_timings' },
        { icon: <FaLayerGroup />, text: 'Active Plans', path: '/active-plans', permissionId: 'active_plans' },
        { icon: <FaEyeSlash />, text: 'Hidden Items', path: '/hidden-items', permissionId: 'hidden_items' },
        { icon: <FaGift />, text: 'Reward Rules', path: '/rewards', permissionId: 'item_list' },
        { icon: <FaPlusCircle />, text: 'Create Reward', path: '/create-reward', permissionId: 'item_list' },
        { icon: <FaCog />, text: 'Reward Settings', path: '/reward-settings', permissionId: 'item_list' },
      ],
    },
    { icon: <FaShoppingCart />, text: 'Purchase', permissionId: 'purchase', moduleKey: 'purchase', path: '/purchase' },
    {
      icon: <FaBox />, text: 'Stock', permissionId: 'purchase', moduleKey: 'purchase',
      children: [
        { icon: <FaList />, text: 'Stock Balance', path: '/stock-balance', permissionId: 'purchase' },
        { icon: <FaFileAlt />, text: 'Stock Ledger', path: '/stock-ledger', permissionId: 'purchase' },
      ],
    },
    {
      icon: <FaMapMarkerAlt />, text: 'Address', permissionId: 'address_structure', moduleKey: 'address_module',
      children: [{ icon: <FaMapMarkerAlt />, text: 'Address Structure', path: '/address-structure', permissionId: 'address_structure' }],
    },
    {
      icon: <FaUserTie />, text: 'Employee', permissionId: 'employee_list', moduleKey: 'hr_payroll',
      children: [
        { icon: <FaList />, text: 'Employee List', path: '/employee-list', permissionId: 'employee_list' },
        { icon: <FaUserTie />, text: 'Delivery Person', path: '/employees', permissionId: 'delivery_persons' },
        { icon: <FaUsers />, text: 'Users', path: '/users', permissionId: 'user_management' },
        { icon: <FaPlusCircle />, text: 'Add New Employee', path: '/add-employee', permissionId: 'add_employee' },
        { icon: <FaBriefcase />, text: 'Employee Designations', path: '/employee-designations', permissionId: 'employee_designations' },
        { icon: <FaIdCard />, text: 'Employee Types', path: '/employee-types', permissionId: 'employee_types' },
        { icon: <FaBuilding />, text: 'Employee Departments', path: '/employee-departments', permissionId: 'employee_departments' },
        { icon: <FaCalendarCheck />, text: 'Roaster App', path: '/roaster', permissionId: 'roaster' },
        { icon: <FaCar />, text: 'Vehicle Management', path: '/vehicle-management', permissionId: 'vehicle_management' },
        {
          icon: <FaClock />, text: 'Schedule', permissionId: 'schedule_master',
          children: [
            { icon: <FaCalendar />, text: 'Schedule Master', path: '/schedule-master', permissionId: 'schedule_master' },
            { icon: <FaUserPlus />, text: 'Schedule Assign Employee', path: '/schedule-assign-employee', permissionId: 'schedule_assign' },
          ],
        },
        {
          icon: <FaClock />, text: 'Attendance Management', permissionId: 'view_attendance',
          children: [
            { icon: <FaPlusCircle />, text: 'Create Attendance', path: '/attendance', permissionId: 'create_attendance' },
            { icon: <FaEye />, text: 'View Attendance', path: '/attendance-view', permissionId: 'view_attendance' },
          ],
        },
        {
          icon: <FaCalendar />, text: 'Leave Management', permissionId: 'leave_apply',
          children: [
            { icon: <FaList />, text: 'Leave Types', path: '/leave-type', permissionId: 'leave_types' },
            { icon: <FaCreditCard />, text: 'Leave Allocation', path: '/leave-allocation', permissionId: 'leave_allocation' },
            { icon: <FaPaperPlane />, text: 'Leave Apply', path: '/leave-apply', permissionId: 'leave_apply' },
          ],
        },
        {
          icon: <FaMoneyCheckAlt />, text: 'Salary Management', permissionId: 'salary_list',
          children: [
            { icon: <FaPlusCircle />, text: 'Salary Receipt', path: '/salary-slip', permissionId: 'salary_slip' },
            { icon: <FaList />, text: 'Salary Receipt List', path: '/salary-receipt-list', permissionId: 'salary_list' },
          ],
        },
      ],
    },
    { icon: <FaTable />, text: 'Add Table', path: '/add-table', permissionId: 'add_table', moduleKey: 'table_management' },
    {
      icon: <FaCog />, text: 'Settings', permissionId: 'settings', moduleKey: 'settings',
      children: [
        { icon: <FaBuilding />, text: 'Company Details', path: '/company-details', permissionId: 'company_details' },
        { icon: <FaLayerGroup />, text: 'DocType Management', path: '/doctype', permissionId: 'doctype' },
        { icon: <FaPrint />, text: 'Print Settings', path: '/print-settings', permissionId: 'print_settings' },
        { icon: <FaEnvelope />, text: 'Email Settings', path: '/email-settings', permissionId: 'email_settings' },
        { icon: <FaWhatsapp />, text: 'WhatsApp Settings', path: '/whatsapp-settings', permissionId: 'whatsapp_settings' },
        { icon: <FaDatabase />, text: 'Backups', path: '/backup', permissionId: 'backup' },
        { icon: <FaTable />, text: 'Advanced Import', path: '/import-data', permissionId: 'import' },
        { icon: <FaCog />, text: 'System Settings', path: '/system-settings', permissionId: 'system_settings' },
        { icon: <FaMoneyBill />, text: 'Tax Master', path: '/tax-master', permissionId: 'tax_master' },
      ],
    },
    {
      icon: <FaChartLine />, text: 'Sales Reports', permissionId: 'sales_report', moduleKey: 'reports',
      children: [
        { icon: <FaFileInvoiceDollar />, text: 'Sales Invoice', path: '/salespage', permissionId: 'pos' },
        { icon: <FaChartBar />, text: 'Sales Report', path: '/sales-reports', permissionId: 'sales_report' },
      ],
    },
    {
      icon: <FaUserShield />, text: 'Super Admin', path: '/super-admin', permissionId: 'manage_tenants', roleFilter: ['superadmin'], moduleKey: 'multi_branch'
    },
    {
      icon: <FaFileAlt />, text: 'Subscribe', path: '/subscribe', permissionId: 'manage_tenants', roleFilter: ['superadmin'], moduleKey: 'multi_branch'
    },
    {
      icon: <FaImage />, text: 'Item Gallery', path: '/item-gallery', permissionId: 'manage_tenants', roleFilter: ['superadmin'], moduleKey: 'multi_branch'
    },
    {
      icon: <FaBuilding />, text: 'Multi-Company Support', permissionId: 'manage_companies', moduleKey: 'multi_branch',
      roleFilter: ['superadmin', 'groupadmin', 'tenantadmin', 'masteradmin', 'companyadmin', 'branchadmin', 'branchmanager', 'admin'],
      children: [
        {
          icon: <FaBuilding />, text: 'Manage Organization', path: '/manage-tenants', permissionId: 'manage_tenants',
          roleFilter: ['superadmin', 'groupadmin', 'tenantadmin', 'masteradmin']
        },
        {
          icon: <FaBuilding />, text: 'Manage Companies', path: '/manage-companies', permissionId: 'manage_companies',
          roleFilter: ['superadmin', 'groupadmin', 'tenantadmin', 'masteradmin', 'companyadmin']
        },
        {
          icon: <FaLayerGroup />, text: 'Manage Branches', path: '/manage-branches', permissionId: 'manage_branches',
          roleFilter: ['superadmin', 'groupadmin', 'tenantadmin', 'masteradmin', 'companyadmin', 'branchadmin', 'branchmanager', 'admin']
        },
        {
          icon: <FaBuilding />, text: 'Company Details', path: '/company-details', permissionId: 'company_details',
          roleFilter: ['superadmin', 'groupadmin', 'tenantadmin', 'masteradmin', 'companyadmin', 'branchadmin', 'branchmanager', 'admin']
        },
      ],
    },
  ];

  const filterItemsByPermissions = (items, permissions, userRole) => {
    const isAdmin = checkIsAdmin(userRole);
    const isSuperAdmin = String(userRole).toLowerCase().includes('super') || String(userRole).toLowerCase().includes('group');
    const isAllMode = activeCompany === 'All' &&
      (activeBranch === 'All Branches' || activeBranch === 'All') &&
      (!localStorage.getItem('active_tenant_id') || localStorage.getItem('active_tenant_id') === '');

    return items.reduce((acc, item) => {
      if (item.roleFilter && !item.roleFilter.includes(userRole.toLowerCase().replace(/[\s_]/g, ''))) return acc;

      // Restaurant Plan is only for superadmins — always visible regardless of active company
      if (item.text === 'Restaurant Plan' && !isSuperAdmin) return acc;

      let permitted = false;
      if (isSuperAdmin || isAdmin) {
        permitted = true;
      } else {
        const p = permissions.find(pm => (pm.permissionId === item.permissionId) || (pm.pageId === item.permissionId));
        if (p && p.canRead) permitted = true;
      }

      // Check if target module is disabled in Workflow Settings
      const target = item.moduleKey || item.path;
      if (target && !isPageEnabled(target, userRole)) return acc;

      // Process children recursively
      let permittedChildren = [];
      if (item.children) {
        permittedChildren = filterItemsByPermissions(item.children, permissions, userRole);
        // If the parent didn't have explicit permission, but a child does, the parent MUST be visible
        if (permittedChildren.length > 0) {
          permitted = true;
        }
      }

      // If neither the parent nor any children are permitted, hide it
      if (!permitted) return acc;

      if (item.children) {
        if (permittedChildren.length === 0) return acc;
        return [...acc, { ...item, children: permittedChildren }];
      }

      return [...acc, item];
    }, []);
  };

  const applyPermissions = async (currentBaseUrl = baseUrl) => {
    const userStored = localStorage.getItem('user'); if (!userStored) return;
    const userObj = JSON.parse(userStored);
    const role = userObj.role || userObj.user_role || userObj.role_name || '';

    const activeComp = localStorage.getItem('active_company') || userObj.company_name || userObj.company;
    const activeBranch = userObj.branch_name || userObj.branch;

    const headers = {};
    if (activeComp) headers['X-Company-Name'] = activeComp;
    if (activeBranch && activeBranch !== 'All Branches') headers['X-Branch-Name'] = activeBranch;

    try {
      const res = await axios.get(`${currentBaseUrl}/api/role-permissions?role=${encodeURIComponent(role)}&t=${Date.now()}`, { headers });
      setUserPermissions(res.data.permissions || []);
      const allowed = filterItemsByPermissions(masterMenuItems, res.data.permissions || [], role);
      setFilteredMasterMenuItems(allowed);
    } catch (err) {
      if (role.toLowerCase().includes('admin')) {
        setFilteredMasterMenuItems(masterMenuItems.filter(it => !it.path || isPageEnabled(it.path, role)));
      }
    }
  };

  const hasRolePermission = (permId) => {
    const role = user?.role || '';
    if (checkIsAdmin(role) || String(role).toLowerCase().includes('super') || String(role).toLowerCase().includes('group')) return true;
    const p = userPermissions.find(pm => pm.permissionId === permId || pm.pageId === permId);
    return p ? p.canRead : false;
  };

  const [allMenuOrders, setAllMenuOrders] = useState(() => {
    const saved = localStorage.getItem('adminAllMenuOrders');
    return saved ? JSON.parse(saved) : {};
  });

  const handleDragStart = (e, index, parentKey) => {
    e.dataTransfer.setData('draggedIndex', index);
    e.dataTransfer.setData('parentKey', parentKey);
    e.currentTarget.style.opacity = '0.4';
    e.stopPropagation();
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    const items = document.querySelectorAll('.draggable-node');
    items.forEach(item => { item.style.borderTop = 'none'; item.style.boxShadow = 'none'; });
  };

  const handleDragOver = (e, index, parentKey) => {
    e.preventDefault(); e.stopPropagation();
    const items = document.querySelectorAll(`[data-parent="${parentKey}"]`);
    items.forEach((item, i) => {
      if (i === index) {
        item.style.borderTop = '2px solid #3498db';
        item.style.boxShadow = '0 -4px 8px rgba(52, 152, 219, 0.2)';
      } else { item.style.borderTop = 'none'; item.style.boxShadow = 'none'; }
    });
  };

  const handleDrop = (e, targetIndex, parentKey, itemsInThisList) => {
    e.preventDefault(); e.stopPropagation();
    const draggedIndexStr = e.dataTransfer.getData('draggedIndex');
    const draggedParentKey = e.dataTransfer.getData('parentKey');
    if (!draggedIndexStr || draggedParentKey !== parentKey) return;
    const draggedIndex = parseInt(draggedIndexStr);
    if (draggedIndex === targetIndex) return;

    const itemText = itemsInThisList[draggedIndex].text;
    const targetText = itemsInThisList[targetIndex].text;

    setAllMenuOrders(prev => {
      const updatedAll = { ...prev };
      const currentOrder = updatedAll[parentKey] ? [...updatedAll[parentKey]] : itemsInThisList.map(it => it.text);
      const fromIdx = currentOrder.indexOf(itemText);
      const toIdx = currentOrder.indexOf(targetText);
      if (fromIdx !== -1 && toIdx !== -1) {
        currentOrder.splice(fromIdx, 1);
        currentOrder.splice(toIdx, 0, itemText);
        updatedAll[parentKey] = currentOrder;
        localStorage.setItem('adminAllMenuOrders', JSON.stringify(updatedAll));
      }
      return updatedAll;
    });

    const items = document.querySelectorAll('.draggable-node');
    items.forEach(item => { item.style.borderTop = 'none'; item.style.boxShadow = 'none'; });
  };

  const sortRecursive = (items, parentKey) => {
    const order = allMenuOrders[parentKey];
    const sorted = [...items];
    if (order) {
      sorted.sort((a, b) => {
        const idxA = order.indexOf(a.text);
        const idxB = order.indexOf(b.text);
        if (idxA === -1 && idxB === -1) return 0;
        if (idxA === -1) return 1;
        if (idxB === -1) return -1;
        return idxA - idxB;
      });
    }
    return sorted.map(item => {
      if (item.children) return { ...item, children: sortRecursive(item.children, item.text) };
      return item;
    });
  };

  const filterMenu = (items, query) => {
    const lowerQuery = query.toLowerCase();
    return items.reduce((acc, item) => {
      if (item.children) {
        const filteredChildren = item.children.filter(child => child.text.toLowerCase().includes(lowerQuery));
        if (item.text.toLowerCase().includes(lowerQuery) || filteredChildren.length > 0) {
          acc.push({ ...item, children: filteredChildren });
        }
      } else if (item.text.toLowerCase().includes(lowerQuery)) {
        acc.push(item);
      }
      return acc;
    }, []);
  };

  const finalMenuData = useMemo(() => {
    const sorted = sortRecursive(filteredMasterMenuItems, 'root');
    return searchQuery ? filterMenu(sorted, searchQuery) : sorted;
  }, [filteredMasterMenuItems, allMenuOrders, searchQuery]);

  const getExpandState = (text) => {
    switch (text) {
      case 'Customers': return isCustomersOpen; case 'Items': return isItemsOpen; case 'Stock': return isStockOpen;
      case 'Employee': return isEmployeeAppOpen; case 'Settings': return isSettingsOpen;
      case 'Sales Reports': return isSalesOpen; case 'Schedule': return isScheduleOpen;
      case 'Attendance Management': return isAttendanceOpen; case 'Leave Management': return isLeaveOpen;
      case 'Salary Management': return isSalaryOpen; case 'Address': return isAddressOpen;
      case 'Multi-Company Support': return isMultiCompanyOpen; default: return false;
    }
  };

  const toggleExpandState = (text) => {
    switch (text) {
      case 'Customers': setIsCustomersOpen(!isCustomersOpen); break;
      case 'Stock': setIsStockOpen(!isStockOpen); break;
      case 'Items': setIsItemsOpen(!isItemsOpen); break;
      case 'Employee': setIsEmployeeAppOpen(!isEmployeeAppOpen); break;
      case 'Settings': setIsSettingsOpen(!isSettingsOpen); break;
      case 'Sales Reports': setIsSalesOpen(!isSalesOpen); break;
      case 'Schedule': setIsScheduleOpen(!isScheduleOpen); break;
      case 'Attendance Management': setIsAttendanceOpen(!isAttendanceOpen); break;
      case 'Leave Management': setIsLeaveOpen(!isLeaveOpen); break;
      case 'Salary Management': setIsSalaryOpen(!isSalaryOpen); break;
      case 'Address': setIsAddressOpen(!isAddressOpen); break;
      case 'Multi-Company Support': setIsMultiCompanyOpen(!isMultiCompanyOpen); break;
    }
  };

  useEffect(() => { applyPermissions(); }, [user, workflowSettings, disabledPages]);

  const getFormattedDate = (date, dateFormat) => {
    if (!dateFormat) return date.toLocaleDateString();
    const parts = new Intl.DateTimeFormat('en', { year: 'numeric', month: '2-digit', day: 'numeric' }).formatToParts(date);
    const y = parts.find(p => p.type === 'year')?.value || '';
    const m = parts.find(p => p.type === 'month')?.value || '';
    const d = parts.find(p => p.type === 'day')?.value || '';
    switch (dateFormat) {
      case 'dd-mm-yyyy': return `${d}-${m}-${y}`;
      case 'mm-dd-yyyy': return `${m}-${d}-${y}`;
      case 'yyyy-mm-dd': return `${y}-${m}-${d}`;
      default: return date.toLocaleDateString();
    }
  };

  const getFormattedTime = (date, timeFormat) => {
    const is12Hour = timeFormat?.includes(' a') || timeFormat?.startsWith('hh');
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: is12Hour });
  };

  const handleLogoutClick = () => setShowLogoutConfirm(true);
  const confirmLogout = () => { setShowLogoutConfirm(false); navigate('/login'); };
  const cancelLogout = () => setShowLogoutConfirm(false);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file && allowedFile(file.name)) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => { setPreviewUrl(reader.result); setMessage(''); setError(null); };
      reader.readAsDataURL(file);
    } else setError('Please select a valid image file');
  };

  const handleSaveLogo = async () => {
    if (!logoFile) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      const h = getHeaders(activeBranch !== 'All Branches' ? activeBranch : null, activeCompany);

      // CRITICAL: Delete any explicit Content-Type from getHeaders so the browser
      // can automatically inject 'multipart/form-data' with the correct boundary string.
      // If we don't do this, the backend will reject it with a 400 Bad Request.
      const safeHeaders = { ...h };
      delete safeHeaders['Content-Type'];

      const response = await axios.post(`${baseUrl}/api/upload-logo`, formData, {
        headers: safeHeaders
      });
      if (response.data && response.data.logo) {
        setLogoUrl(response.data.logo);
        setPreviewUrl(response.data.logo);
        setLogoFile(null); // Clear pending file
        setMessage('Logo uploaded successfully');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error uploading logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleNavigation = (path) => {
    if (path === '/items' || path === '/items-list' || path === '/inventory' || path === '/create-item') {
      if (!kitchensExist) {
        setPendingPath(path);
        setSetupStep(1);
        setShowSetupPrompt(true);
        return;
      }
      if (!groupsExist) {
        setPendingPath(path);
        setSetupStep(2);
        setShowSetupPrompt(true);
        return;
      }
    }
    if (path === '/add-employee' || path === '/employee-list' || path === '/employees') {
      if (!typesExist) {
        setPendingPath(path);
        setSetupStep(3);
        setShowSetupPrompt(true);
        return;
      }
      if (!departmentsExist) {
        setPendingPath(path);
        setSetupStep(4);
        setShowSetupPrompt(true);
        return;
      }
      if (!designationsExist) {
        setPendingPath(path);
        setSetupStep(5);
        setShowSetupPrompt(true);
        return;
      }
    }
    navigate(path, { state: { fromAdmin: true } });
  };
  const toggleMasterMenu = () => setIsMasterOpen(!isMasterOpen);
  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const allowedFile = (filename) => {
    const allowed = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'jfif', 'ico'];
    return allowed.some(ext => filename.toLowerCase().endsWith(`.${ext}`));
  };
  const handleDeleteLogo = async () => {
    const isConfirmed = window.confirm("Are you sure you want to delete this logo?");
    if (!isConfirmed) return;

    try {
      const h = getHeaders(activeBranch !== 'All Branches' ? activeBranch : null, activeCompany);
      const response = await axios.delete(`${baseUrl}/api/delete-logo`, { headers: h });
      setMessage(response.data.message); setLogoUrl(null); setPreviewUrl(null); setLogoFile(null);
    } catch (err) { setError(err.message); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.name.endsWith('.json') || file.name.endsWith('.xlsx'))) {
      setImportFile(file); setMessage(''); setError(null);
    } else setError('Please select a valid file');
  };

  const renderDraggableMenuItem = (item, index, parentKey, itemsInList) => {
    // SYNC SIDEBAR WITH MANAGES CONFIGURATION
    if (!isPageEnabled(item.path || item.text, user?.role)) return null;

    const isExpanded = getExpandState(item.text);

    const handleToggleExpand = () => {
      if (!isExpanded && (item.text === 'Items' || item.text === 'Inventory')) {
        if (!kitchensExist) {
          toggleExpandState(item.text);
          setPendingPath(null);
          setSetupStep(1);
          setShowSetupPrompt(true);
          return;
        }
        if (!groupsExist) {
          toggleExpandState(item.text);
          setPendingPath(null);
          setSetupStep(2);
          setShowSetupPrompt(true);
          return;
        }
      }
      if (!isExpanded && (item.text === 'Employee' || item.text === 'HR & Payroll')) {
        if (!typesExist) {
          toggleExpandState(item.text);
          setPendingPath(null);
          setSetupStep(3);
          setShowSetupPrompt(true);
          return;
        }
        if (!departmentsExist) {
          toggleExpandState(item.text);
          setPendingPath(null);
          setSetupStep(4);
          setShowSetupPrompt(true);
          return;
        }
        if (!designationsExist) {
          toggleExpandState(item.text);
          setPendingPath(null);
          setSetupStep(5);
          setShowSetupPrompt(true);
          return;
        }
      }
      toggleExpandState(item.text);
    };
    const hasChildren = item.children && item.children.length > 0;

    return (
      <div key={item.text} className="draggable-node" data-parent={parentKey} draggable={!searchQuery} onDragStart={(e) => handleDragStart(e, index, parentKey)} onDragEnd={handleDragEnd} onDragOver={(e) => handleDragOver(e, index, parentKey)} onDrop={(e) => handleDrop(e, index, parentKey, itemsInList)} style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
        {hasChildren ? (
          <>
            {searchQuery ? (
              <div title={isSidebarCollapsed ? item.text : ''} style={{ padding: isSidebarCollapsed ? '10px' : (adminDesign === 'design2' ? '10px 14px' : '12px 16px'), backgroundColor: adminDesign === 'design2' ? '#0E4231' : 'var(--ap-bg)', color: adminDesign === 'design2' ? '#FFFFFF' : 'var(--ap-text-primary)', borderRadius: '12px', fontSize: adminDesign === 'design2' ? '14px' : '15px', display: 'flex', alignItems: 'center', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', gap: '12px', fontWeight: '700', fontFamily: adminDesign === 'design2' ? "'Plus Jakarta Sans', sans-serif" : 'inherit' }}>
                <span style={{ fontSize: '18px', display: 'flex', alignItems: 'center', color: adminDesign === 'design2' ? '#34D399' : 'inherit' }}>{item.icon}</span> {!isSidebarCollapsed && item.text}
              </div>
            ) : (
              <button onClick={handleToggleExpand} title={isSidebarCollapsed ? item.text : ''} style={{ padding: isSidebarCollapsed ? '10px' : (adminDesign === 'design2' ? '10px 14px' : '12px 16px'), backgroundColor: isExpanded && !isSidebarCollapsed ? (adminDesign === 'design2' ? '#0E4231' : 'var(--ap-bg)') : 'transparent', color: adminDesign === 'design2' ? '#CBD5E1' : 'var(--ap-text-primary)', border: 'none', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', gap: '12px', fontSize: adminDesign === 'design2' ? '14px' : '15px', fontWeight: '600', fontFamily: adminDesign === 'design2' ? "'Plus Jakarta Sans', sans-serif" : 'inherit', textAlign: 'left', width: '100%', transition: 'all 0.2s' }} onMouseOver={(e) => { if (!isExpanded && !isSidebarCollapsed) { e.currentTarget.style.backgroundColor = adminDesign === 'design2' ? '#0E4231' : 'var(--ap-bg)'; e.currentTarget.style.color = adminDesign === 'design2' ? '#FFFFFF' : 'var(--ap-primary-color)'; } }} onMouseOut={(e) => { if (!isExpanded) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = adminDesign === 'design2' ? '#CBD5E1' : 'var(--ap-text-primary)'; } }}>
                <span style={{ fontSize: '18px', display: 'flex', alignItems: 'center', color: adminDesign === 'design2' ? '#A7F3D0' : 'inherit' }}>{item.icon}</span>
                {!isSidebarCollapsed && (
                  <>
                    {item.text}
                    {isExpanded ? <FaChevronUp style={{ marginLeft: 'auto', color: adminDesign === 'design2' ? '#A7F3D0' : 'var(--ap-text-secondary)', fontSize: '12px' }} /> : <FaChevronDown style={{ marginLeft: 'auto', color: adminDesign === 'design2' ? '#A7F3D0' : 'var(--ap-text-secondary)', fontSize: '12px' }} />}
                  </>
                )}
              </button>
            )}
            {((searchQuery && item.children.length > 0) || (isExpanded && item.children.length > 0)) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: isSidebarCollapsed ? '12px' : '8px' }}>
                {item.children.map((child, idx) => renderDraggableMenuItem(child, idx, item.text, item.children))}
              </div>
            )}
          </>
        ) : parentKey === 'root' ? (
          <button onClick={() => handleNavigation(item.path)} title={isSidebarCollapsed ? item.text : ''} style={{ padding: isSidebarCollapsed ? '10px' : (adminDesign === 'design2' ? '10px 14px' : '12px 16px'), backgroundColor: 'transparent', color: adminDesign === 'design2' ? '#CBD5E1' : 'var(--ap-text-primary)', border: 'none', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', gap: '12px', fontSize: adminDesign === 'design2' ? '14px' : '15px', fontWeight: '600', fontFamily: adminDesign === 'design2' ? "'Plus Jakarta Sans', sans-serif" : 'inherit', textAlign: 'left', width: '100%', transition: 'all 0.2s' }} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = adminDesign === 'design2' ? '#0E4231' : 'var(--ap-bg)'; e.currentTarget.style.color = adminDesign === 'design2' ? '#FFFFFF' : 'var(--ap-primary-color)'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = adminDesign === 'design2' ? '#CBD5E1' : 'var(--ap-text-primary)'; }}>
            <span style={{ fontSize: '18px', display: 'flex', alignItems: 'center', color: adminDesign === 'design2' ? '#A7F3D0' : 'inherit' }}>{item.icon}</span> {!isSidebarCollapsed && item.text}
          </button>
        ) : (
          <button onClick={() => handleNavigation(item.path)} title={isSidebarCollapsed ? item.text : ''} style={{ padding: isSidebarCollapsed ? '8px' : (adminDesign === 'design2' ? '8px 14px 8px 36px' : '10px 16px 10px 40px'), backgroundColor: 'transparent', color: adminDesign === 'design2' ? '#94A3B8' : 'var(--ap-text-secondary)', border: 'none', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: isSidebarCollapsed ? 'center' : 'flex-start', gap: '10px', fontSize: adminDesign === 'design2' ? '13px' : '14px', fontWeight: '500', fontFamily: adminDesign === 'design2' ? "'Plus Jakarta Sans', sans-serif" : 'inherit', textAlign: 'left', width: '100%', transition: 'all 0.2s' }} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = adminDesign === 'design2' ? '#0E4231' : 'var(--ap-bg)'; e.currentTarget.style.color = adminDesign === 'design2' ? '#FFFFFF' : 'var(--ap-primary-color)'; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = adminDesign === 'design2' ? '#94A3B8' : 'var(--ap-text-secondary)'; }}>
            <span style={{ fontSize: '16px', display: 'flex', alignItems: 'center', color: adminDesign === 'design2' ? '#A7F3D0' : 'inherit' }}>{item.icon}</span> {!isSidebarCollapsed && item.text}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={`admin-page-root ${layoutMode}-layout theme-${theme}`} style={{ display: 'flex', minHeight: '100vh', backgroundColor: adminDesign === 'design2' ? '#F7F6F0' : 'var(--ap-bg)', fontFamily: adminDesign === 'design2' ? "'Plus Jakarta Sans', sans-serif" : "'Inter', sans-serif", color: 'var(--ap-text-primary)', transition: 'background-color 0.3s, color 0.3s' }}>
      <div className={`admin-sidebar ${adminDesign === 'design2' ? 'mc-sidebar-theme' : ''}`} style={{ width: isSidebarCollapsed ? '90px' : '270px', transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)', backgroundColor: adminDesign === 'design2' ? '#083324' : 'var(--ap-card)', borderRight: adminDesign === 'design2' ? '1px solid #144a39' : '1px solid var(--ap-border)', padding: isSidebarCollapsed ? '24px 10px' : '24px 20px', height: '100vh', position: 'fixed', left: 0, top: 0, display: isBoxViewActive ? 'none' : 'flex', flexDirection: 'column', overflow: 'visible', zIndex: 1000, boxSizing: 'border-box', fontFamily: adminDesign === 'design2' ? "'Plus Jakarta Sans', sans-serif" : 'inherit' }}>

        {/* Collapse Toggle Arrow */}
        <button onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} style={{ position: 'absolute', top: '30px', right: '-12px', width: '24px', height: '24px', backgroundColor: adminDesign === 'design2' ? '#0A3D2B' : 'var(--ap-card)', border: adminDesign === 'design2' ? '1px solid #165640' : '1px solid var(--ap-border)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10, color: adminDesign === 'design2' ? '#34D399' : 'var(--ap-primary-color)', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
          {isSidebarCollapsed ? <FaChevronRight style={{ fontSize: '10px' }} /> : <FaChevronLeft style={{ fontSize: '10px' }} />}
        </button>

        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingRight: '5px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Top Logo / Brand section matching screenshot */}
          {adminDesign === 'design2' ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: isSidebarCollapsed ? '8px 4px' : '4px 6px 14px 6px',
              borderBottom: '1px solid #144E39',
              marginBottom: '2px'
            }}>
              <div style={{
                width: isSidebarCollapsed ? '36px' : '38px',
                height: isSidebarCollapsed ? '36px' : '38px',
                minWidth: isSidebarCollapsed ? '36px' : '38px',
                backgroundColor: '#F59E0B',
                color: '#083324',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                overflow: 'hidden'
              }}>
                {(logoUrl || previewUrl) ? (
                  <img src={previewUrl || logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <FaUtensils />
                )}
              </div>
              {!isSidebarCollapsed && (
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#FFFFFF', lineHeight: 1.2, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {activeCompany && activeCompany !== 'All' ? activeCompany : 'Restaurant Manager'}
                  </div>
                  <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '1px', color: '#6EE7B7', marginTop: '2px', textTransform: 'uppercase', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    MANAGER CONSOLE
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: isSidebarCollapsed ? '8px' : '12px', backgroundColor: 'var(--ap-bg)', borderRadius: '16px', border: '1px solid var(--ap-border)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {(logoUrl || previewUrl) ? (
                <img src={previewUrl || logoUrl} alt="Logo" style={{ width: isSidebarCollapsed ? '40px' : '70px', height: isSidebarCollapsed ? '40px' : '70px', transition: 'all 0.3s', borderRadius: '50%', objectFit: 'cover', marginBottom: isSidebarCollapsed ? '0' : '10px', border: '2px solid var(--ap-primary-color)' }} />
              ) : (<div style={{ width: isSidebarCollapsed ? '40px' : '70px', height: isSidebarCollapsed ? '40px' : '70px', transition: 'all 0.3s', borderRadius: '50%', backgroundColor: 'var(--ap-border)', margin: isSidebarCollapsed ? '0' : '0 auto 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ap-text-secondary)', fontSize: isSidebarCollapsed ? '0.8rem' : '1.5rem', fontWeight: 'bold' }}>Logo</div>)}

              {!isSidebarCollapsed && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <input id="logo-input" type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />

                  {!logoUrl && !logoFile && (
                    <label htmlFor="logo-input" style={{ padding: '6px 12px', backgroundColor: 'var(--ap-primary-color)', color: 'white', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}><FaCamera /> Upload</label>
                  )}

                  {logoFile && (
                    <button onClick={handleSaveLogo} disabled={uploadingLogo} style={{ padding: '6px 12px', backgroundColor: '#34C759', color: 'white', border: 'none', borderRadius: '8px', cursor: uploadingLogo ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '600' }}>
                      {uploadingLogo ? 'Saving...' : 'Save'}
                    </button>
                  )}

                  {logoUrl && !logoFile && (
                    <button onClick={handleDeleteLogo} style={{ padding: '6px 12px', backgroundColor: 'var(--ap-danger)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}><FaTrash /> Delete</button>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={{ position: 'relative' }}>
            <FaSearch style={{ position: 'absolute', left: isSidebarCollapsed ? '50%' : '14px', top: '50%', transform: isSidebarCollapsed ? 'translate(-50%, -50%)' : 'translateY(-50%)', color: adminDesign === 'design2' ? '#6EE7B7' : 'var(--ap-text-secondary)' }} />
            {!isSidebarCollapsed && (
              <input type="text" placeholder="Search menu..." value={searchQuery} onChange={handleSearchChange} style={{ width: '100%', padding: '10px 14px 10px 40px', borderRadius: '12px', border: adminDesign === 'design2' ? '1px solid #165640' : '1px solid var(--ap-border)', backgroundColor: adminDesign === 'design2' ? '#0A3D2B' : 'var(--ap-bg)', outline: 'none', fontSize: '13px', color: adminDesign === 'design2' ? '#FFFFFF' : 'var(--ap-text-primary)', fontWeight: '500', fontFamily: adminDesign === 'design2' ? "'Plus Jakarta Sans', sans-serif" : 'inherit', boxSizing: 'border-box' }} />
            )}
            {isSidebarCollapsed && (
              <div style={{ width: '100%', height: '38px', borderRadius: '12px', border: adminDesign === 'design2' ? '1px solid #165640' : '1px solid var(--ap-border)', backgroundColor: adminDesign === 'design2' ? '#0A3D2B' : 'var(--ap-bg)' }} />
            )}
          </div>

          {!isSidebarCollapsed && (user.role === 'group_admin' || user.role === 'superadmin' || user.role === 'Super Admin' || user.role === 'Tenant Admin' || user.role === 'company_admin' || user.role === 'Company Admin') && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {(user.role === 'Super Admin' || user.role === 'superadmin') && user.tenants?.length > 0 && (
                <select
                  value={localStorage.getItem('active_tenant_id') || ''}
                  onChange={(e) => {
                    const tenantId = e.target.value;
                    const tenantName = e.target.options[e.target.selectedIndex].text;
                    localStorage.setItem('active_tenant_id', tenantId);
                    localStorage.setItem('active_tenant_name', tenantName);
                    localStorage.removeItem('active_company_id');
                    localStorage.removeItem('active_branch_id');
                    window.dispatchEvent(new Event('local-storage-change'));
                    window.location.reload();
                  }}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: adminDesign === 'design2' ? '1px solid #165640' : '1px solid var(--ap-border)', backgroundColor: adminDesign === 'design2' ? '#0A3D2B' : 'var(--ap-card)', cursor: 'pointer', fontWeight: '600', color: adminDesign === 'design2' ? '#FFFFFF' : 'var(--ap-text-primary)', fontSize: '13px' }}
                >
                  <option value="">All Organizations</option>
                  {user.tenants.map(t => <option key={t._id} value={t._id}>{t.tenant_name}</option>)}
                </select>
              )}

              {(user.role === 'group_admin' || user.role === 'superadmin' || user.role === 'Super Admin' || user.role === 'Tenant Admin') && adminCompanies.length > 0 && (
                <select value={activeCompany} onChange={(e) => handleCompanySwitch(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: adminDesign === 'design2' ? '1px solid #165640' : '1px solid var(--ap-border)', backgroundColor: adminDesign === 'design2' ? '#0A3D2B' : 'var(--ap-card)', cursor: 'pointer', fontWeight: '600', color: adminDesign === 'design2' ? '#FFFFFF' : 'var(--ap-text-primary)', fontSize: '13px' }}>
                  <option value="All">All Companies</option>
                  {adminCompanies.map((c, i) => <option key={i} value={c}>{c}</option>)}
                </select>
              )}

              {activeCompany && activeCompany !== 'All' &&
                ((user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'groupadmin' ||
                  (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'superadmin' ||
                  (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'tenantadmin' ||
                  (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'masteradmin' ||
                  (user?.role || '').toLowerCase().replace(/[\s_]/g, '') === 'companyadmin' ||
                  (user?.role || '').toLowerCase() === 'super admin' ||
                  (user?.role || '').toLowerCase() === 'tenant admin') && (
                  <select value={activeBranch} onChange={(e) => handleBranchSwitch(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: adminDesign === 'design2' ? '1px solid #165640' : '1px solid var(--ap-border)', backgroundColor: adminDesign === 'design2' ? '#0A3D2B' : 'var(--ap-card)', cursor: 'pointer', fontWeight: '600', color: adminDesign === 'design2' ? '#FFFFFF' : 'var(--ap-text-primary)', fontSize: '13px' }}>
                    <option value="All Branches">All Branches</option>
                    {branches
                      .filter(b => {
                        if (typeof b === 'string') return true;
                        const branchCompany = b.company_name || b.company || '';
                        return activeCompany === 'All' || branchCompany === activeCompany;
                      })
                      .map((b, i) => {
                        const bName = b.branch_name || b.branch || b.name || b;
                        return <option key={i} value={bName}>{bName}</option>;
                      })
                    }
                  </select>
                )}
            </div>
          )}

          {/* Active Dashboard / Overview Button matching screenshot */}
          <button title={isSidebarCollapsed ? (adminDesign === 'design2' ? 'Overview' : 'Dashboard') : ''} style={{
            padding: isSidebarCollapsed ? '10px' : (adminDesign === 'design2' ? '10px 14px' : '14px 20px'),
            backgroundColor: adminDesign === 'design2' ? '#144A39' : 'var(--ap-primary-light)',
            color: adminDesign === 'design2' ? '#FFFFFF' : 'var(--ap-primary-color)',
            border: adminDesign === 'design2' ? '1px solid #1F5E48' : 'none',
            borderRadius: adminDesign === 'design2' ? '12px' : '16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
            gap: '12px',
            fontSize: adminDesign === 'design2' ? '14px' : '16px',
            fontWeight: '700',
            width: '100%',
            boxShadow: adminDesign === 'design2' ? '0 2px 8px rgba(0, 0, 0, 0.2)' : '0 4px 15px rgba(96, 75, 232, 0.1)',
            transition: 'all 0.2s',
            fontFamily: adminDesign === 'design2' ? "'Plus Jakarta Sans', sans-serif" : 'inherit'
          }}>
            {adminDesign === 'design2' ? (
              <div style={{
                width: '28px',
                height: '28px',
                minWidth: '28px',
                borderRadius: '8px',
                backgroundColor: '#F59E0B',
                color: '#083324',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '13px'
              }}>
                <FaChartBar />
              </div>
            ) : (
              <FaChartBar style={{ fontSize: '20px', color: 'var(--ap-primary-color)' }} />
            )}
            {!isSidebarCollapsed && (adminDesign === 'design2' ? 'Overview' : 'Dashboard')}
          </button>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
            <button onClick={() => toggleMasterMenu()} title={isSidebarCollapsed ? 'Master' : ''} style={{
              padding: isSidebarCollapsed ? '10px' : (adminDesign === 'design2' ? '10px 14px' : '12px 16px'),
              backgroundColor: isMasterOpen && !isSidebarCollapsed ? (adminDesign === 'design2' ? '#0E4231' : 'var(--ap-bg)') : 'transparent',
              color: adminDesign === 'design2' ? '#FFFFFF' : 'var(--ap-text-primary)',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
              gap: '12px',
              fontSize: adminDesign === 'design2' ? '14px' : '15px',
              fontWeight: '600',
              fontFamily: adminDesign === 'design2' ? "'Plus Jakarta Sans', sans-serif" : 'inherit',
              transition: 'all 0.2s'
            }}>
              <FaHome style={{ color: adminDesign === 'design2' ? '#34D399' : 'var(--ap-text-secondary)', fontSize: '18px' }} />
              {!isSidebarCollapsed && (
                <>
                  Master
                  {isMasterOpen ? (
                    <FaChevronUp style={{ marginLeft: 'auto', color: adminDesign === 'design2' ? '#34D399' : 'var(--ap-text-secondary)', fontSize: '12px' }} />
                  ) : (
                    <FaChevronDown style={{ marginLeft: 'auto', color: adminDesign === 'design2' ? '#34D399' : 'var(--ap-text-secondary)', fontSize: '12px' }} />
                  )}
                </>
              )}
            </button>
            {isMasterOpen && finalMenuData.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: isSidebarCollapsed ? '12px' : '8px' }}>
                {finalMenuData.map((item, idx) => renderDraggableMenuItem(item, idx, "root", finalMenuData))}
              </div>
            )}
          </div>

          {/* User Profile Card at bottom of sidebar matching image */}
          <div style={{ marginTop: 'auto', padding: isSidebarCollapsed ? '8px' : '16px', backgroundColor: adminDesign === 'design2' ? '#0A3D2B' : 'var(--ap-bg)', borderRadius: '20px', border: adminDesign === 'design2' ? '1px solid #165640' : '1px solid var(--ap-border)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '12px', transition: 'all 0.3s' }}>
            <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(user.email || 'Theresa')}`} alt="User Avatar" style={{ width: isSidebarCollapsed ? '40px' : '54px', height: isSidebarCollapsed ? '40px' : '54px', borderRadius: '50%', backgroundColor: adminDesign === 'design2' ? '#083324' : 'var(--ap-border)', border: adminDesign === 'design2' ? '2px solid #165640' : '2px solid var(--ap-card)', boxShadow: 'var(--ap-shadow-light)', transition: 'all 0.3s' }} />
            {!isSidebarCollapsed && (
              <div>
                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: adminDesign === 'design2' ? '#FFFFFF' : 'var(--ap-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>{user.name || user.email?.split('@')[0] || 'Theresa Webb'}</h4>
                <p style={{ margin: '2px 0 0', fontSize: '12px', fontWeight: '600', color: adminDesign === 'design2' ? '#34D399' : 'var(--ap-text-secondary)', textTransform: 'capitalize' }}>{user.role || 'Designation'}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginLeft: isBoxViewActive ? '0' : (isSidebarCollapsed ? '90px' : '270px'), transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)', flex: 1, padding: adminDesign === 'design2' ? '0' : '36px 48px', backgroundColor: adminDesign === 'design2' ? '#F7F6F0' : 'var(--ap-bg)', minHeight: '100vh', position: 'relative', boxSizing: 'border-box', overflowX: 'hidden' }}>
        {adminDesign === 'design2' ? (
          <ManagerConsoleDashboard
            embedded={true}
            user={user}
            calculatedSalesMetrics={calculatedSalesMetrics}
            calculatedPurchaseMetrics={calculatedPurchaseMetrics}
            salesData={salesData}
            purchaseInvoices={purchaseInvoices}
            purchaseItems={purchaseItems}
            stockBalanceData={stockBalanceData}
            stockLedgerData={stockLedgerData}
            suppliers={suppliers}
            activeOrdersData={activeOrdersData}
            itemsData={itemsData}
            customersData={customersData}
            posSessionsData={posSessionsData}
            timeframe={timeframe}
            setTimeframe={setTimeframe}
            selectedDashboardDate={selectedDashboardDate}
            setSelectedDashboardDate={setSelectedDashboardDate}
            adminDesign={adminDesign}
            setAdminDesign={setAdminDesign}
            onLogout={handleLogoutClick}
            onRefresh={() => fetchDashboardData(activeCompany, activeBranch, adminCompanies)}
            activeCompany={activeCompany}
            activeBranch={activeBranch}
            unreadCount={unreadCount}
          />
        ) : (
          <>
            {/* Top bar with Notifications, User Info, Logout */}
        <div className="modern-full-topbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', zIndex: 100, width: '100%' }}>
          {/* LEFT SIDE: LOGO */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {layoutMode === 'modern' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingRight: '20px', borderRight: '1px solid var(--ap-border)' }}>
                {(logoUrl || previewUrl) ? (
                  <img src={previewUrl || logoUrl} alt="Logo" style={{ height: '40px', width: '40px', objectFit: 'cover', borderRadius: '12px', border: '2px solid var(--ap-primary-color)' }} />
                ) : (
                  <div style={{ width: '40px', height: '40px', backgroundColor: 'var(--ap-primary-color)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold' }}>L</div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT SIDE: TOOLS, USER INFO, LOGOUT */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {layoutMode !== 'modern' && (
              <div onClick={() => setIsBoxViewActive(!isBoxViewActive)} style={{ marginLeft: '20px', marginRight: '20px', cursor: 'pointer', backgroundColor: isBoxViewActive ? 'var(--ap-primary-color)' : 'var(--ap-card)', color: isBoxViewActive ? 'var(--ap-card)' : 'var(--ap-primary-color)', padding: '10px 16px', borderRadius: '12px', boxShadow: 'var(--ap-shadow-light)', border: '1px solid var(--ap-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: '700', fontSize: '14px', transition: 'all 0.3s' }}>
                <FaLayerGroup /> {isBoxViewActive ? 'Exit Box View' : 'Box View'}
              </div>
            )}

            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginRight: '20px' }}>
              {/* Design 1 / Design 2 Toggle Pill */}
              <div style={{ display: 'flex', backgroundColor: 'var(--ap-card)', padding: '3px', borderRadius: '20px', border: '1px solid var(--ap-border)', boxShadow: 'var(--ap-shadow-light)' }} title="Toggle between Design 1 (Classic) and Design 2 (Manager Console)">
                <button
                  onClick={() => setAdminDesign('design1')}
                  style={{
                    border: 'none',
                    background: adminDesign === 'design1' ? 'var(--ap-primary-color)' : 'transparent',
                    color: adminDesign === 'design1' ? '#FFFFFF' : 'var(--ap-text-secondary)',
                    padding: '6px 14px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Design 1
                </button>
                <button
                  onClick={() => setAdminDesign('design2')}
                  style={{
                    border: 'none',
                    background: adminDesign === 'design2' ? '#083324' : 'transparent',
                    color: adminDesign === 'design2' ? '#FFFFFF' : 'var(--ap-text-secondary)',
                    padding: '6px 14px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Design 2
                </button>
              </div>

              <div onClick={() => setLayoutMode(layoutMode === 'classic' ? 'modern' : 'classic')} title="Toggle Layout Design" style={{ cursor: 'pointer', backgroundColor: 'var(--ap-card)', padding: '12px', borderRadius: '50%', boxShadow: 'var(--ap-shadow-light)', border: '1px solid var(--ap-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', color: 'var(--ap-primary-color)' }}>
                <FaMagic style={{ fontSize: '1.2rem', transition: 'transform 0.3s ease', transform: layoutMode === 'modern' ? 'rotate(180deg)' : 'none' }} />
              </div>

              <div style={{ position: 'relative' }} ref={themeDropdownRef}>
                <div onClick={() => setShowThemeDropdown(!showThemeDropdown)} style={{ cursor: 'pointer', backgroundColor: showThemeDropdown ? 'var(--ap-primary-light)' : 'var(--ap-card)', padding: '12px', borderRadius: '50%', boxShadow: 'var(--ap-shadow-light)', border: `1px solid ${showThemeDropdown ? 'var(--ap-primary-color)' : 'var(--ap-border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', color: showThemeDropdown ? 'var(--ap-primary-color)' : 'var(--ap-text-primary)' }}>
                  <FaPalette style={{ fontSize: '1.2rem' }} />
                </div>
              {showThemeDropdown && (
                <div className="admin-theme-dropdown">
                  <div className={`admin-theme-option ${theme === 'light' ? 'active' : ''}`} onClick={() => { setTheme('light'); setShowThemeDropdown(false); }}>
                    <span style={{ width: '24px', textAlign: 'center' }}><FaSun /></span> Light
                  </div>
                  <div className={`admin-theme-option ${theme === 'ocean' ? 'active' : ''}`} onClick={() => { setTheme('ocean'); setShowThemeDropdown(false); }}>
                    <span style={{ width: '24px', textAlign: 'center' }}><FaWater /></span> Ocean
                  </div>
                  <div className={`admin-theme-option ${theme === 'dark' ? 'active' : ''}`} onClick={() => { setTheme('dark'); setShowThemeDropdown(false); }}>
                    <span style={{ width: '24px', textAlign: 'center' }}><FaMoon /></span> Dark
                  </div>
                  <div className={`admin-theme-option ${theme === 'sunset' ? 'active' : ''}`} onClick={() => { setTheme('sunset'); setShowThemeDropdown(false); }}>
                    <span style={{ width: '24px', textAlign: 'center' }}><FaCloudSun /></span> Sunset
                  </div>
                </div>
              )}
              </div>
            </div>

            <div onClick={() => { if (typeof handleNavigation === 'function') handleNavigation('/notifications'); }} style={{ marginRight: '20px', cursor: 'pointer', position: 'relative', backgroundColor: 'var(--ap-card)', padding: '12px', borderRadius: '50%', boxShadow: 'var(--ap-shadow-light)', border: '1px solid var(--ap-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaBell style={{ color: 'var(--ap-primary-color)', fontSize: '1.2rem' }} />
              {unreadCount > 0 && <span style={{ position: 'absolute', top: '-6px', right: '-6px', backgroundColor: 'var(--ap-danger)', color: 'white', borderRadius: '50%', minWidth: '20px', height: '20px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white', fontWeight: 'bold' }}>{unreadCount}</span>}
            </div>

            <div style={{ textAlign: 'right', marginRight: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <span style={{ fontWeight: '700', color: 'var(--ap-text-primary)', fontSize: '15px' }}>{user.email}</span>
                  <span style={{ fontSize: '11px', color: 'var(--ap-primary-color)', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{activeCompany} {activeBranch && activeBranch !== 'All Branches' ? `| ${activeBranch}` : ''}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '4px' }}>
                <small style={{ color: 'var(--ap-text-secondary)', fontWeight: '600', fontSize: '12px' }}>{getFormattedDate(currentTime, settings.dateFormat)}</small>
                <small style={{ color: 'var(--ap-text-secondary)', fontWeight: '600', fontSize: '12px' }}>{getFormattedTime(currentTime, settings.timeFormat)}</small>
              </div>
            </div>
            
            <button onClick={handleLogoutClick} title="Logout" style={{ background: 'transparent', border: 'none', padding: '0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', opacity: 0.9 }} onMouseOver={(e) => { e.currentTarget.style.opacity = 1; e.currentTarget.style.transform = 'scale(1.1)'; }} onMouseOut={(e) => { e.currentTarget.style.opacity = 0.9; e.currentTarget.style.transform = 'scale(1)'; }}>
              <img src="/menuIcons/poweroff.svg" alt="Logout" style={{ width: '36px', height: '36px' }} />
            </button>
          </div>
        </div>

        {/* Modern Layout Navigation Grid */}
        {layoutMode === 'modern' && !isBoxViewActive && (
          <div style={{ marginBottom: '40px', position: 'relative' }}>
            {/* Invisible overlay for click-outside to close */}
            {activeModernParent && (
              <div 
                onClick={() => setActiveModernParent(null)} 
                style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 90 }}
              />
            )}
            
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '20px', position: 'relative', zIndex: 100 }}>
              <h2 style={{ color: 'var(--ap-text-primary)', margin: 0, fontSize: '24px', fontWeight: '800', textAlign: 'center', animation: 'slideUpFade 0.3s forwards' }} key={activeModernParent ? activeModernParent.text : 'main'}>
                {activeModernParent ? activeModernParent.text : 'Main Menu'}
              </h2>
              {activeModernParent && (
                <button 
                  onClick={() => setActiveModernParent(null)} 
                  title="Close and return to Main Menu" 
                  style={{ position: 'absolute', right: '0', backgroundColor: 'var(--ap-danger)', color: '#fff', border: 'none', borderRadius: '50%', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(255, 59, 48, 0.4)', transition: 'all 0.2s', zIndex: 101 }}
                  onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <FaTimes style={{ fontSize: '14px' }} />
                </button>
              )}
            </div>
            
            <div className="modern-grid-nav" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', position: 'relative', zIndex: 100 }}>
              {(() => {
                const items = activeModernParent ? activeModernParent.children : finalMenuData;
                const rows = [];
                let count = 5;
                let index = 0;
                while (index < items.length) {
                  rows.push(items.slice(index, index + count));
                  index += count;
                  count = Math.max(1, count - 1);
                }
                
                let globalIdx = 0;
                return rows.map((row, rowIdx) => (
                  <div key={rowIdx} style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                    {row.map((item, colIdx) => {
                      const idx = globalIdx++;
                      return (
                        <div 
                          key={idx + (activeModernParent ? activeModernParent.text : 'main')} 
                          onClick={() => { 
                            if (item.children && item.children.length > 0) {
                              setActiveModernParent(item);
                            } else {
                              if (activeModernParent) setActiveModernParent(null);
                              if (typeof handleNavigation === 'function') {
                                handleNavigation(item.path); 
                              } else if (item.path) {
                                navigate(item.path);
                              }
                            }
                          }} 
                          className="modern-grid-box" 
                          style={{ width: '130px', backgroundColor: 'var(--ap-card)', padding: '16px 12px', borderRadius: '20px', border: '1px solid var(--ap-border)', boxShadow: 'var(--ap-shadow-heavy)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)', animation: `slideUpFade 0.3s cubic-bezier(0.16, 1, 0.3, 1) ${idx * 0.03}s forwards`, opacity: 0, backdropFilter: 'blur(16px)' }}
                        >
                          <div style={{ fontSize: '24px', color: 'var(--ap-primary-color)' }}>{item.icon}</div>
                          <h4 style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: 'var(--ap-text-primary)', textAlign: 'center', lineHeight: '1.2' }}>{item.text}</h4>
                        </div>
                      );
                    })}
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {isBoxViewActive ? (
          <div className="box-view-container" style={{ padding: '20px 0', position: 'relative' }}>
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '40px', minHeight: '52px' }}>
              <h2 style={{ margin: 0, color: 'var(--ap-text-primary)', fontSize: '32px', fontWeight: '900', letterSpacing: '1px', textAlign: 'center' }}>
                {boxViewSearchQuery ? 'Search Results' : 'Main Menu'}
              </h2>
              <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', width: '100%', maxWidth: '300px' }}>
                <FaSearch style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--ap-text-secondary)', fontSize: '16px' }} />
                <input
                  type="text"
                  placeholder="Search menu..."
                  value={boxViewSearchQuery}
                  onChange={(e) => setBoxViewSearchQuery(e.target.value)}
                  style={{ width: '100%', padding: '14px 16px 14px 48px', borderRadius: '16px', border: '1px solid var(--ap-border)', backgroundColor: 'var(--ap-card)', outline: 'none', fontSize: '15px', color: 'var(--ap-text-primary)', fontWeight: '500', boxSizing: 'border-box', boxShadow: 'var(--ap-shadow-light)', transition: 'all 0.3s' }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--ap-primary-color)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(96, 75, 232, 0.15)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--ap-border)'; e.currentTarget.style.boxShadow = 'var(--ap-shadow-light)'; }}
                />
              </div>
            </div>

            {boxViewActiveParent && !boxViewSearchQuery ? (
              (() => {
                const totalChildren = boxViewActiveParent.children.length;
                const orbitRadius = Math.max(240, totalChildren * 32); // Reduced radius multiplier for tighter gaps
                const containerMinHeight = Math.max(650, orbitRadius * 2 + 180);
                const containerMaxWidth = Math.max(1100, orbitRadius * 2 + 200); // Dynamically expand horizontal width if needed

                return (
                  <div onClick={() => setBoxViewActiveParent(null)} style={{ backgroundColor: 'var(--ap-bg)', borderRadius: '24px', border: '1px solid var(--ap-border-strong)', padding: '40px', boxShadow: 'var(--ap-shadow-heavy)', maxWidth: `${containerMaxWidth}px`, width: '100%', margin: '0 auto', minHeight: `${containerMinHeight}px`, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.3s ease' }}>

                    {/* Top Right Close Button */}
                    <button onClick={(e) => { e.stopPropagation(); setBoxViewActiveParent(null); }} style={{ position: 'absolute', top: '24px', right: '24px', width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'var(--ap-danger-light)', color: 'var(--ap-danger)', border: 'none', fontSize: '24px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 30, transition: 'all 0.2s' }} onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'} onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                      &times;
                    </button>

                    {/* Connecting Lines */}
                    <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' }}>
                      {boxViewActiveParent.children.map((child, idx) => {
                        const angle = (idx / totalChildren) * (2 * Math.PI) - (Math.PI / 2);
                        const x = Math.cos(angle) * orbitRadius;
                        const y = Math.sin(angle) * orbitRadius;
                        return (
                          <line
                            key={idx}
                            x1="50%"
                            y1="50%"
                            x2={`calc(50% + ${x}px)`}
                            y2={`calc(50% + ${y}px)`}
                            stroke="var(--ap-border-strong)"
                            strokeWidth="2"
                            strokeDasharray="4 4"
                          />
                        );
                      })}
                    </svg>

                    {/* Center Main Item */}
                    <div onClick={(e) => e.stopPropagation()} style={{ zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: 'var(--ap-card)', padding: '32px 40px', borderRadius: '24px', boxShadow: 'var(--ap-shadow-heavy)', border: '2px solid var(--ap-primary-color)', cursor: 'default', transition: 'all 0.3s' }}>
                      <div style={{ width: '80px', height: '80px', borderRadius: '24px', backgroundColor: 'var(--ap-primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px', color: 'var(--ap-primary-color)', marginBottom: '16px' }}>
                        {boxViewActiveParent.icon}
                      </div>
                      <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: 'var(--ap-text-primary)' }}>{boxViewActiveParent.text}</h2>
                    </div>

                    {/* Orbiting Children */}
                    {boxViewActiveParent.children.map((child, idx) => {
                      const angle = (idx / totalChildren) * (2 * Math.PI) - (Math.PI / 2);
                      const x = Math.cos(angle) * orbitRadius;
                      const y = Math.sin(angle) * orbitRadius;

                      return (
                        <div key={idx} onClick={(e) => {
                          e.stopPropagation();
                          if (typeof handleNavigation === 'function') handleNavigation(child.path); else if (child.path) navigate(child.path);
                        }} style={{ position: 'absolute', top: '50%', left: '50%', transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`, backgroundColor: 'var(--ap-card)', borderRadius: '16px', padding: '16px 12px', border: '1px solid var(--ap-border)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', width: '160px', height: '120px', boxShadow: 'var(--ap-shadow-light)', zIndex: 5, animation: `popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${idx * 0.05}s backwards` }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--ap-primary-color)'; e.currentTarget.style.borderColor = 'var(--ap-primary-color)'; e.currentTarget.children[0].style.color = 'var(--ap-card)'; e.currentTarget.children[1].style.color = 'var(--ap-card)'; e.currentTarget.style.boxShadow = 'var(--ap-shadow-heavy)'; e.currentTarget.style.zIndex = 20; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--ap-card)'; e.currentTarget.style.borderColor = 'var(--ap-border)'; e.currentTarget.children[0].style.color = 'var(--ap-primary-color)'; e.currentTarget.children[1].style.color = 'var(--ap-text-primary)'; e.currentTarget.style.boxShadow = 'var(--ap-shadow-light)'; e.currentTarget.style.zIndex = 5; }}>
                          <div style={{ fontSize: '28px', color: 'var(--ap-primary-color)', transition: 'color 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}>
                            {child.icon}
                          </div>
                          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: 'var(--ap-text-primary)', transition: 'color 0.2s', textAlign: 'center', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.2' }}>{child.text}</h4>
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '24px', maxWidth: '1100px', margin: '0 auto' }}>
                {(() => {
                  let itemsToRender = [];

                  if (boxViewSearchQuery) {
                    const flatten = (items) => {
                      let flat = [];
                      items.forEach(item => {
                        if (item.path && item.text.toLowerCase().includes(boxViewSearchQuery.toLowerCase())) {
                          flat.push(item);
                        }
                        if (item.children) {
                          flat = flat.concat(flatten(item.children));
                        }
                      });
                      return flat;
                    };
                    itemsToRender = flatten(finalMenuData);
                  } else {
                    itemsToRender = finalMenuData;
                  }

                  if (itemsToRender.length === 0) {
                    return <div style={{ width: '100%', textAlign: 'center', padding: '40px', color: 'var(--ap-text-secondary)', fontWeight: '600', fontSize: '16px' }}>No items found.</div>;
                  }

                  // Dynamic Pyramid Layout
                  let rows = [];
                  if (!boxViewSearchQuery) {
                    const N = itemsToRender.length;
                    let k = 1;
                    while (k * (k + 1) / 2 < N) {
                      k++;
                    }

                    let i = 0;
                    let currentSize = k;

                    while (i < N) {
                      let take = Math.min(currentSize, N - i);
                      rows.push(itemsToRender.slice(i, i + take));
                      i += take;
                      currentSize--;
                      if (currentSize < 1) currentSize = 1;
                    }
                  } else {
                    rows = [itemsToRender];
                  }

                  return rows.map((rowItems, rowIndex) => (
                    <div key={rowIndex} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '24px', width: '100%' }}>
                      {rowItems.map((item, idx) => (
                        <div key={idx} style={{
                          width: '180px',
                          backgroundColor: 'var(--ap-card)',
                          borderRadius: '24px',
                          padding: '24px 16px',
                          boxShadow: '0 6px 20px rgba(0,0,0,0.04)',
                          border: '1px solid var(--ap-border)',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '16px',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                          onClick={() => {
                            if (!boxViewSearchQuery && (item.text === 'Items' || item.text === 'Inventory')) {
                              if (!kitchensExist) {
                                setBoxViewActiveParent(item);
                                setPendingPath(null);
                                setSetupStep(1);
                                setShowSetupPrompt(true);
                                return;
                              }
                              if (!groupsExist) {
                                setBoxViewActiveParent(item);
                                setPendingPath(null);
                                setSetupStep(2);
                                setShowSetupPrompt(true);
                                return;
                              }
                            }
                            if (!boxViewSearchQuery && (item.text === 'Employee' || item.text === 'HR & Payroll')) {
                              if (!typesExist) {
                                setBoxViewActiveParent(item);
                                setPendingPath(null);
                                setSetupStep(3);
                                setShowSetupPrompt(true);
                                return;
                              }
                              if (!departmentsExist) {
                                setBoxViewActiveParent(item);
                                setPendingPath(null);
                                setSetupStep(4);
                                setShowSetupPrompt(true);
                                return;
                              }
                              if (!designationsExist) {
                                setBoxViewActiveParent(item);
                                setPendingPath(null);
                                setSetupStep(5);
                                setShowSetupPrompt(true);
                                return;
                              }
                            }
                            if (item.children && item.children.length > 0 && !boxViewSearchQuery) {
                              setBoxViewActiveParent(item);
                            } else {
                              if (typeof handleNavigation === 'function') handleNavigation(item.path); else if (item.path) navigate(item.path);
                            }
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-6px)';
                            e.currentTarget.style.boxShadow = '0 12px 35px rgba(96, 75, 232, 0.12)';
                            e.currentTarget.style.borderColor = 'var(--ap-primary-color)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.04)';
                            e.currentTarget.style.borderColor = 'var(--ap-border)';
                          }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                            <div style={{ width: '64px', height: '64px', borderRadius: '20px', backgroundColor: 'var(--ap-primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', color: 'var(--ap-primary-color)', transition: 'all 0.3s' }}>
                              {item.icon}
                            </div>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: 'var(--ap-text-primary)', textAlign: 'center', letterSpacing: '0.3px' }}>{item.text}</h3>
                          </div>
                        </div>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        ) : layoutMode === 'modern' ? (
          <ModernDashboard 
            calculatedSalesMetrics={calculatedSalesMetrics}
            calculatedPurchaseMetrics={calculatedPurchaseMetrics}
            calculatedStockBalanceMetrics={calculatedStockBalanceMetrics}
            calculatedStockLedgerMetrics={calculatedStockLedgerMetrics}
            timeframe={timeframe}
            setTimeframe={setTimeframe}
            dashboardViewType={dashboardViewType}
            setDashboardViewType={setDashboardViewType}
          />
        ) : (
          <>
            <div style={{ position: 'relative', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '36px', flexWrap: 'wrap', gap: '20px' }}>
              <div className={layoutMode === 'modern' ? 'modern-dashboard-header-container' : ''}>
                <h1 style={{ margin: 0, fontSize: '34px', fontWeight: '800', color: 'var(--ap-text-primary)', letterSpacing: '-0.5px' }}>Dashboard</h1>
                {layoutMode === 'modern' && (
                  <select 
                    value={dashboardViewType}
                    onChange={(e) => setDashboardViewType(e.target.value)}
                    className="modern-dashboard-selector"
                  >
                    <option value="salesreport">Sales Report</option>
                    <option value="purchase">Purchase</option>
                    <option value="salesinvoice">Sales Invoice</option>
                  </select>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '28px', borderBottom: '1px solid var(--ap-border)', paddingBottom: '8px' }}>
                  {['Yesterday', 'Today', 'Week', 'Month', 'Year'].map((tab) => {
                    const isActive = timeframe === tab;
                    return (
                      <div
                        key={tab}
                        onClick={() => setTimeframe(tab)}
                        style={{
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: isActive ? '700' : '600',
                          color: isActive ? 'var(--ap-text-primary)' : 'var(--ap-text-secondary)',
                          position: 'relative',
                          paddingBottom: '8px',
                          transition: 'all 0.2s'
                        }}
                      >
                        {tab}
                        {isActive && (
                          <div style={{ position: 'absolute', bottom: '-9px', left: 0, width: '100%', height: '3px', backgroundColor: '#FF9F04', borderRadius: '3px' }} />
                        )}
                      </div>
                    );
                  })}
                </div>
                {['Week', 'Month', 'Year'].includes(timeframe) && (
                  <DashboardTimePicker timeframe={timeframe} selectedDate={selectedDashboardDate} onChange={setSelectedDashboardDate} />
                )}
              </div>
            </div>

            {/* Top Metric Cards Row */}
            <style>{`
              @keyframes slideUpFadeDash {
                0% { opacity: 0; transform: translateY(40px); }
                100% { opacity: 1; transform: translateY(0); }
              }
              @keyframes progressGrow {
                0% { width: 0; }
              }
              .modern-dash-card {
                background-color: var(--ap-card);
                border-radius: 24px;
                padding: 28px;
                border: 1px solid var(--ap-border);
                box-shadow: 0 8px 30px rgba(0,0,0,0.04);
                transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                opacity: 0;
                animation: slideUpFadeDash 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                backdrop-filter: blur(10px);
              }
              .modern-dash-card:hover {
                transform: translateY(-8px) scale(1.02);
                box-shadow: var(--ap-shadow-heavy);
                border-color: var(--ap-primary-color);
              }
              .modern-progress-bar {
                animation: progressGrow 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
              }
            `}</style>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '28px', marginBottom: '36px' }}>

              {/* Daily Sales Card */}
              <div className="modern-dash-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gridColumn: 'span 2', animationDelay: '0.1s' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--ap-text-primary)' }}>
                      {layoutMode === 'modern' ? (dashboardViewType === 'purchase' ? 'Daily Purchases' : dashboardViewType === 'salesinvoice' ? 'Daily Invoices' : 'Daily Sales') : 'Daily Sales'}
                    </h3>
                    <p style={{ margin: '8px 0 4px', fontSize: '32px', fontWeight: '800', color: 'var(--ap-primary-color)' }}>{calculatedSalesMetrics.salesAmount}</p>
                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: 'var(--ap-text-secondary)' }}>
                      <span style={{ color: 'var(--ap-primary-color)', fontWeight: '700' }}>↑ 2.1%</span> vs last week
                    </p>
                  </div>
                  <button className="modern-view-report-btn" onClick={() => navigate('/sales-reports')}>
                    View Report
                  </button>
                </div>

                {/* Custom SVG Line Chart matching image */}
                <div style={{ marginTop: '24px', width: '100%', height: '120px', position: 'relative' }}>
                  <svg viewBox="0 0 500 120" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                    {/* Grid lines */}
                    <line x1="0" y1="20" x2="500" y2="20" stroke="var(--ap-bg)" strokeDasharray="4 4" strokeWidth="1.5" />
                    <line x1="0" y1="60" x2="500" y2="60" stroke="var(--ap-bg)" strokeDasharray="4 4" strokeWidth="1.5" />
                    <line x1="0" y1="100" x2="500" y2="100" stroke="var(--ap-bg)" strokeDasharray="4 4" strokeWidth="1.5" />

                    {/* Orange Curve (Previous Period) */}
                    <path d={calculatedSalesMetrics.prevGraphPath} fill="none" stroke="var(--ap-danger)" strokeWidth="3" strokeLinecap="round" className={layoutMode === 'modern' ? 'graph-morph-path' : ''} style={{ transition: 'd 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)' }} />

                    {/* Purple Curve (Current Period) */}
                    <path d={calculatedSalesMetrics.graphPath} fill="none" stroke="var(--ap-primary-color)" strokeWidth="3" strokeLinecap="round" className={layoutMode === 'modern' ? 'graph-morph-path' : ''} style={{ transition: 'd 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)' }} />
                  </svg>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', padding: '0 10px', color: 'var(--ap-text-secondary)', fontSize: '11px', fontWeight: '600' }}>
                    <span>09.00 am</span><span>12.00 pm</span><span>03.00 pm</span><span>06.00 pm</span><span>09.00 pm</span><span>12.00 am</span>
                  </div>
                </div>
              </div>

              {/* Total Income Card */}
              <div className="modern-dash-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', animationDelay: '0.2s' }}>
                <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--ap-text-primary)' }}>Total Income</h3>
                  <div style={{ padding: '6px 14px', backgroundColor: 'var(--ap-bg)', border: '1px solid var(--ap-border)', borderRadius: '12px', fontSize: '12px', fontWeight: '700', color: 'var(--ap-primary-color)', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    {timeframe} <FaChevronDown style={{ fontSize: '10px' }} />
                  </div>
                </div>

                {/* Custom Circular Donut Chart - dynamic per real item groups */}
                <div
                  style={{ position: 'relative', width: '160px', height: '160px', margin: '20px 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  onMouseLeave={() => setHoveredDonutGroup(null)}
                >
                  <svg
                    viewBox="0 0 100 100"
                    style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)', overflow: 'visible' }}
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.closest('div')?.getBoundingClientRect();
                      if (rect) {
                        setDonutGroupPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                      }
                    }}
                  >
                    {(() => {
                      const circumference = 2 * Math.PI * 38; // 238.76
                      let accumulated = 0;
                      return calculatedSalesMetrics.topGroups.map((grp, i) => {
                        const dash = (grp.pct / 100) * circumference;
                        const gap = circumference - dash;
                        const offset = -(accumulated / 100) * circumference;
                        accumulated += grp.pct;
                        const isHovered = hoveredDonutGroup?.name === grp.name;
                        return (
                          <circle key={i} cx="50" cy="50" r="38" fill="none"
                            stroke={grp.color}
                            strokeWidth={isHovered ? 17 : 14}
                            strokeDasharray={`${dash} ${gap}`}
                            strokeDashoffset={offset}
                            style={{
                              cursor: 'pointer',
                              transition: 'stroke-width 0.25s ease, opacity 0.2s ease, filter 0.2s ease',
                              opacity: hoveredDonutGroup ? (isHovered ? 1 : 0.6) : 1,
                              filter: isHovered ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))' : 'none'
                            }}
                            onMouseEnter={(e) => {
                              setHoveredDonutGroup(grp);
                              const rect = e.currentTarget.closest('div')?.getBoundingClientRect();
                              if (rect) {
                                setDonutGroupPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                              }
                            }}
                          />
                        );
                      });
                    })()}
                  </svg>
                  <div style={{ position: 'absolute', textAlign: 'center', pointerEvents: 'none' }}>
                    <span style={{ fontSize: '20px', fontWeight: '800', color: 'var(--ap-text-primary)', lineHeight: 1.2 }}>{calculatedSalesMetrics.totalIncome}</span>
                  </div>

                  {/* Hover Tooltip Box */}
                  {hoveredDonutGroup && (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${Math.max(10, Math.min(150, donutGroupPos.x))}px`,
                        top: `${donutGroupPos.y < 45 ? donutGroupPos.y + 16 : donutGroupPos.y - 8}px`,
                        transform: donutGroupPos.y < 45 ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
                        pointerEvents: 'none',
                        zIndex: 100,
                        background: '#FFFFFF',
                        borderRadius: '10px',
                        padding: '8px 14px',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.14), 0 2px 6px rgba(0, 0, 0, 0.06)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
                        {hoveredDonutGroup.name}
                      </div>
                      <div style={{ fontSize: '11.5px', fontWeight: 600, color: '#64748B', marginTop: '3px' }}>
                        {hoveredDonutGroup.pct}% of total sales
                      </div>
                    </div>
                  )}
                </div>

                {/* Dynamic Group Legend */}
                <div style={{ width: '100%', display: 'flex', justifyContent: 'space-around', paddingTop: '12px', borderTop: '1px solid var(--ap-bg)' }}>
                  {calculatedSalesMetrics.topGroups.map((grp, i) => (
                    <div key={i} style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: 'var(--ap-text-secondary)' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: grp.color, flexShrink: 0 }} />
                        <span style={{ maxWidth: '64px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{grp.name}</span>
                      </div>
                      <p style={{ margin: '4px 0 0', fontSize: '14px', fontWeight: '800', color: 'var(--ap-text-primary)' }}>{grp.pct}%</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column: Total Orders & New Customers */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>

                {/* Total Orders Card */}
                <div className="modern-dash-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', animationDelay: '0.3s' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--ap-text-primary)' }}>Total Orders</h3>
                      <p style={{ margin: '4px 0 12px', fontSize: '13px', fontWeight: '700', color: 'var(--ap-text-secondary)' }}>Completed Invoices</p>
                      <p style={{ margin: 0, fontSize: '32px', fontWeight: '800', color: 'var(--ap-text-primary)' }}>{calculatedSalesMetrics.totalOrders}</p>
                    </div>
                    <div style={{ width: '48px', height: '48px', borderRadius: '16px', backgroundColor: 'var(--ap-danger-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ap-danger)', fontSize: '22px' }}>
                      <FaFileInvoiceDollar />
                    </div>
                  </div>
                  {/* Order type breakdown pills */}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
                    <span style={{ padding: '3px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', backgroundColor: 'var(--ap-primary-light)', color: 'var(--ap-primary-color)' }}>DIN {calculatedSalesMetrics.dineInCount}</span>
                    <span style={{ padding: '3px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', backgroundColor: 'var(--ap-danger-light)', color: 'var(--ap-danger)' }}>TAK {calculatedSalesMetrics.takeawayCount}</span>
                    <span style={{ padding: '3px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', backgroundColor: 'var(--ap-primary-light)', color: 'var(--ap-primary-color)' }}>OND {calculatedSalesMetrics.onlineCount}</span>
                  </div>
                  <div style={{ width: '100%', height: '6px', backgroundColor: 'var(--ap-bg)', borderRadius: '3px', marginTop: '12px', overflow: 'hidden' }}>
                    <div className="modern-progress-bar" style={{ width: `${Math.min(100, (parseInt(calculatedSalesMetrics.totalOrders.replace(/,/g, '')) / Math.max(1, parseInt(calculatedSalesMetrics.totalOrders.replace(/,/g, '')))) * 100)}%`, height: '100%', backgroundColor: 'var(--ap-danger)', borderRadius: '3px' }} />
                  </div>
                </div>

                {/* New Customers Card */}
                <div className="modern-dash-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', animationDelay: '0.4s' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--ap-text-primary)' }}>New Customers</h3>
                      <p style={{ margin: '4px 0 12px', fontSize: '13px', fontWeight: '700', color: 'var(--ap-primary-color)' }}>+32.40%</p>
                      <p style={{ margin: 0, fontSize: '32px', fontWeight: '800', color: 'var(--ap-text-primary)' }}>{calculatedSalesMetrics.newCustomers}</p>
                    </div>
                    <div style={{ width: '48px', height: '48px', borderRadius: '16px', backgroundColor: 'var(--ap-primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ap-primary-color)', fontSize: '22px' }}>
                      <FaUsers />
                    </div>
                  </div>
                  <div style={{ width: '100%', height: '6px', backgroundColor: 'var(--ap-bg)', borderRadius: '3px', marginTop: '16px', overflow: 'hidden' }}>
                    <div className="modern-progress-bar" style={{ width: '85%', height: '100%', backgroundColor: 'var(--ap-primary-color)', borderRadius: '3px' }} />
                  </div>
                </div>

              </div>

            </div>

            {/* Bottom Grid Rows */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '28px', marginBottom: '40px' }}>

              {/* Trending Dishes Card */}
              <div className="modern-dash-card" style={{ animationDelay: '0.5s' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--ap-text-primary)' }}>Trending Dishes</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ padding: '6px 14px', backgroundColor: 'var(--ap-bg)', border: '1px solid var(--ap-border)', borderRadius: '12px', fontSize: '12px', fontWeight: '700', color: 'var(--ap-primary-color)', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'default' }}>
                      {timeframe} <FaChevronDown style={{ fontSize: '10px' }} />
                    </div>
                    <button
                      onClick={() => navigate('/admin/trending-dishes', {
                        state: {
                          initialTimeframe: timeframe,
                          selectedDashboardDate,
                          passedSalesData: salesData,
                          passedItemsData: itemsData,
                          passedCompany: activeCompany,
                          passedBranch: activeBranch
                        }
                      })}
                      style={{
                        padding: '6px 14px',
                        backgroundColor: 'var(--ap-primary-color, #604BE8)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(96,75,232,0.25)',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(96,75,232,0.35)'; }}
                      onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(96,75,232,0.25)'; }}
                    >
                      <FaEye style={{ fontSize: '12px' }} /> View All
                    </button>
                  </div>
                </div>

                {calculatedSalesMetrics.trendingDishes.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--ap-text-secondary)', fontSize: '14px', fontWeight: '600' }}>
                    No sales data for this period
                  </div>
                ) : (
                  /* paddingTop gives room for the floating rank badges, overflow:visible lets them show */
                  <div style={{ display: 'flex', gap: '14px', overflowX: 'auto', paddingTop: '20px', paddingBottom: '8px', alignItems: 'stretch' }}>
                    {calculatedSalesMetrics.trendingDishes.map((dish, idx) => {
                      const badgeColors = ['var(--ap-primary-color)', '#FF9F04', '#06D6A0', 'var(--ap-danger)', '#3B82F6', '#F59E0B'];
                      const badgeBg = badgeColors[idx % badgeColors.length];
                      const rankEmojis = ['🥇', '🥈', '🥉'];
                      return (
                        <div key={idx} style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center',
                          justifyContent: 'flex-start',
                          background: idx === 0 ? 'linear-gradient(160deg, var(--ap-primary-light) 0%, #EDE9FE 100%)' : 'var(--ap-bg)',
                          border: `2px solid ${idx === 0 ? 'var(--ap-primary-color)' : 'var(--ap-border)'}`,
                          borderRadius: '20px',
                          padding: '24px 14px 20px',
                          minWidth: '130px', flex: '1 1 0',
                          minHeight: '230px',
                          boxShadow: idx === 0 ? '0 8px 24px rgba(96,75,232,0.15)' : '0 2px 8px rgba(0,0,0,0.04)',
                          position: 'relative',
                          cursor: 'default',
                          transition: 'transform 0.2s, box-shadow 0.2s'
                        }}
                          onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 14px 32px rgba(96,75,232,0.22)'; }}
                          onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = idx === 0 ? '0 8px 24px rgba(96,75,232,0.15)' : '0 2px 8px rgba(0,0,0,0.04)'; }}
                        >
                          {/* Rank Badge — floating above card top edge */}
                          <div style={{
                            position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)',
                            background: badgeBg, color: '#fff', borderRadius: '20px',
                            fontSize: '11px', fontWeight: '900', padding: '4px 12px',
                            whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                            lineHeight: 1.4, zIndex: 2
                          }}>
                            {rankEmojis[idx] || `#${idx + 1}`}
                          </div>

                          {/* Dish Image — always same size and position */}
                          <div style={{ width: '72px', height: '72px', borderRadius: '50%', marginBottom: '10px', marginTop: '4px', flexShrink: 0, overflow: 'hidden', border: `3px solid ${idx === 0 ? 'var(--ap-primary-color)' : 'var(--ap-border)'}`, background: badgeBg + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {dish.imageUrl ? (
                              <img
                                src={dish.imageUrl} alt={dish.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={e => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '🍽️'; }}
                              />
                            ) : (
                              <span style={{ fontSize: '28px' }}>🍽️</span>
                            )}
                          </div>

                          {/* Category Tag */}
                          <span style={{
                            padding: '3px 10px', borderRadius: '8px', fontSize: '10px', fontWeight: '800',
                            backgroundColor: badgeBg, color: '#fff',
                            display: 'block', marginBottom: '8px', textAlign: 'center',
                            maxWidth: '110px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                          }}>
                            {dish.realGroup || dish.classifiedLabel}
                          </span>

                          {/* Dish Name — fixed 2-line clamp so all cards same height */}
                          <h4 style={{
                            margin: '0', fontSize: '13px', fontWeight: '700', color: 'var(--ap-text-primary)',
                            textAlign: 'center', lineHeight: 1.35,
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                            overflow: 'hidden', maxWidth: '110px',
                            minHeight: '36px', /* 2 lines × 1.35 line-height × 13px */
                            flex: 1 /* push divider + count to bottom */
                          }}>
                            {dish.name}
                          </h4>

                          {/* Divider */}
                          <div style={{ width: '100%', height: '1px', backgroundColor: idx === 0 ? '#D0C8F8' : 'var(--ap-border)', margin: '12px 0' }} />

                          {/* Orders Count — always at the bottom */}
                          <div style={{ textAlign: 'center', width: '100%' }}>
                            <p style={{ margin: 0, fontSize: '10px', fontWeight: '800', color: 'var(--ap-text-secondary)', textTransform: 'uppercase', letterSpacing: '1px' }}>Orders</p>
                            <p style={{ margin: '5px 0 0', fontSize: '28px', fontWeight: '900', color: idx === 0 ? 'var(--ap-primary-color)' : 'var(--ap-text-primary)', lineHeight: 1 }}>{dish.quantity}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>

            {/* Existing Admin Management Section */}
            <div style={{ marginTop: '20px' }}>
              <h3 style={{ color: 'var(--ap-text-primary)', fontSize: '22px', fontWeight: '800', marginBottom: '28px' }}>Admin Management</h3>
              {loading && <div style={{ textAlign: 'center', padding: '20px', fontSize: '16px', color: 'var(--ap-text-secondary)' }}>Loading...</div>}
              {error && <div style={{ backgroundColor: '#ffebee', padding: '16px', color: '#c0392b', borderRadius: '16px', textAlign: 'center', fontWeight: '600' }}>{error}</div>}

              {!loading && !error && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '28px' }}>
                  <DashboardCard icon={<FaUsers />} title="Total Customers" count={customerCount} path="/customers" visible={isPageEnabled('/customers', user?.role) && hasRolePermission('customer_list')} />
                  <DashboardCard icon={<FaBox />} title="Total Items" count={itemCount} path="/items" visible={isPageEnabled('/items', user?.role) && hasRolePermission('item_list')} />
                  <DashboardCard icon={<FaDatabase />} title="Total Backups" count={backupCount} path="/backup" visible={isPageEnabled('/backup', user?.role) && hasRolePermission('backup')} />
                  <DashboardCard icon={<FaTable />} title="Advanced Import" subTitle="Excel / JSON" path="/import-data" visible={isPageEnabled('/import-data', user?.role) && hasRolePermission('import')} />
                  <DashboardCard icon={<FaBuilding />} title="Company Details" path="/company-details" visible={isPageEnabled('/company-details', user?.role) && hasRolePermission('company_details')} />
                </div>
              )}
            </div>
          </>
        )}
          </>
        )}
      </div>

      {showSetupPrompt && (
        <div className="custom-modal-overlay">
          <div className="custom-logout-modal" style={{ border: '2px solid var(--ap-primary-color)', maxWidth: '450px' }}>
            <h2 className="modal-title" style={{ color: 'var(--ap-text-primary)' }}>
              {setupStep === 1 ? 'Kitchen Setup' :
                setupStep === 2 ? 'Item Groups & Addons' :
                  setupStep === 3 ? 'Employee Types' :
                    setupStep === 4 ? 'Departments' : 'Designations'}
            </h2>
            <p className="modal-text" style={{ color: 'var(--ap-text-secondary)', marginTop: '15px', lineHeight: '1.5' }}>
              {setupStep === 1 ? "It looks like you haven't set up any kitchens yet. Does your organization use a Kitchen?" :
                setupStep === 2 ? "You haven't created any Item Groups or Addons. Would you like to set them up before adding your items?" :
                  setupStep === 3 ? "It looks like you haven't created any Employee Types yet. Would you like to set them up?" :
                    setupStep === 4 ? "You haven't created any Departments yet. Would you like to set them up?" :
                      "You haven't created any Employee Designations yet. Would you like to set them up?"}
            </p>
            <div className="modal-buttons">
              <button
                className="confirm-btn"
                style={{ backgroundColor: 'var(--ap-primary-color)', flex: 1 }}
                onClick={() => {
                  setShowSetupPrompt(false);
                  if (setupStep === 1) navigate('/add-kitchen', { state: { fromAdmin: true } });
                  else if (setupStep === 2) navigate('/add-item-group', { state: { fromAdmin: true } });
                  else if (setupStep === 3) navigate('/employee-types', { state: { fromAdmin: true } });
                  else if (setupStep === 4) navigate('/employee-departments', { state: { fromAdmin: true } });
                  else if (setupStep === 5) navigate('/employee-designations', { state: { fromAdmin: true } });
                }}
              >
                {setupStep === 1 ? 'Yes, Add Kitchen' :
                  setupStep === 2 ? 'Yes, Set Up Groups' :
                    setupStep === 3 ? 'Yes, Add Types' :
                      setupStep === 4 ? 'Yes, Add Dept' : 'Yes, Add Desig'}
              </button>
              <button
                className="cancel-btn"
                style={{ flex: 1 }}
                onClick={() => {
                  if (setupStep === 1 && !groupsExist) setSetupStep(2);
                  else if (setupStep === 3 && !departmentsExist) setSetupStep(4);
                  else if ((setupStep === 3 || setupStep === 4) && !designationsExist) setSetupStep(5);
                  else {
                    setShowSetupPrompt(false);
                    if (pendingPath) {
                      navigate(pendingPath, { state: { fromAdmin: true } });
                    }
                  }
                }}
              >
                No, Skip
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogoutConfirm && (<div className="custom-modal-overlay"><div className="custom-logout-modal"><h2 className="modal-title">Logout Confirmation</h2><p className="modal-text">Are you sure you want to logout?</p><div className="modal-buttons"><button className="confirm-btn" onClick={confirmLogout}>YES</button><button className="cancel-btn" onClick={cancelLogout}>NO</button></div></div></div>)}

      <style>{`
        button { font-family: inherit; }
        .custom-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.6); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 2000; }
        .custom-logout-modal { background: var(--ap-card); padding: 40px; border-radius: 24px; text-align: center; border: 2px solid #ef4444; }
        .modal-buttons { display: flex; gap: 15px; justify-content: center; margin-top: 20px; }
        .confirm-btn { background: #ef4444; color: white; padding: 10px 20px; border: none; border-radius: 10px; cursor: pointer; }
        .cancel-btn { background: #eee; padding: 10px 20px; border: none; border-radius: 10px; cursor: pointer; }
        @keyframes pulse-notif { 0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); } 70% { transform: scale(1.1); box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); } 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); } }

        /* Design 2 Sidebar Theme */
        .mc-sidebar-theme::-webkit-scrollbar,
        .mc-sidebar-theme *::-webkit-scrollbar {
          width: 5px;
        }
        .mc-sidebar-theme::-webkit-scrollbar-track,
        .mc-sidebar-theme *::-webkit-scrollbar-track {
          background: #06281c;
        }
        .mc-sidebar-theme::-webkit-scrollbar-thumb,
        .mc-sidebar-theme *::-webkit-scrollbar-thumb {
          background: #14533d;
          border-radius: 4px;
        }
        .mc-sidebar-theme::-webkit-scrollbar-thumb:hover,
        .mc-sidebar-theme *::-webkit-scrollbar-thumb:hover {
          background: #10b981;
        }
        .mc-sidebar-theme select option {
          background-color: #083324 !important;
          color: #ffffff !important;
        }
        .mc-sidebar-theme,
        .mc-sidebar-theme *,
        .mc-sidebar-theme button,
        .mc-sidebar-theme input,
        .mc-sidebar-theme select {
          font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif !important;
          -webkit-font-smoothing: antialiased;
        }
      `}</style>
    </div>
  );

  function DashboardCard({ icon, title, count, subTitle, path, visible }) {
    if (!visible) return null;
    return (
      <div style={{ padding: '28px 24px', backgroundColor: 'var(--ap-card)', borderRadius: '24px', textAlign: 'center', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', border: '1px solid var(--ap-border)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} onClick={() => handleNavigation(path)} onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 15px 45px rgba(96, 75, 232, 0.15)'; e.currentTarget.style.borderColor = 'var(--ap-primary-color)'; }} onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = 'var(--ap-border)'; }}>
        <div style={{ width: '64px', height: '64px', borderRadius: '20px', backgroundColor: 'var(--ap-primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', color: 'var(--ap-primary-color)', marginBottom: '20px' }}>{icon}</div>
        <h4 style={{ margin: '0', color: 'var(--ap-text-secondary)', fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</h4>
        {count !== undefined && (<p style={{ fontSize: '32px', margin: '12px 0 0', color: 'var(--ap-text-primary)', fontWeight: '800', lineHeight: '1' }}>{count}</p>)}
        {subTitle && <p style={{ fontSize: '14px', margin: '12px 0 0', color: 'var(--ap-primary-color)', fontWeight: '700' }}>{subTitle}</p>}
      </div>
    );
  }
}

export default AdminPage;

function ModernDashboard({ calculatedSalesMetrics, calculatedPurchaseMetrics, calculatedStockBalanceMetrics, calculatedStockLedgerMetrics, timeframe, setTimeframe, dashboardViewType, setDashboardViewType }) {
  
    const activeCurrencyCode = resolveCurrencyCode(null, localStorage.getItem('active_company'), localStorage.getItem('active_branch'));
    const activeCurrencySymbol = CURRENCY_SYMBOLS[activeCurrencyCode] || '$';
    
const [graphAnimating, setGraphAnimating] = React.useState(false);
  
  React.useEffect(() => {
    setGraphAnimating(true);
    const t = setTimeout(() => setGraphAnimating(false), 50);
    return () => clearTimeout(t);
  }, [dashboardViewType]);

  const flatPath = "M0,240 L83,240 L166,240 L250,240 L333,240 L416,240 L500,240";

  const isPurchase = dashboardViewType === 'purchase';
  const isStockBalance = dashboardViewType === 'stockbalance';
  const isStockLedger = dashboardViewType === 'stockledger';
  const metrics = isPurchase ? calculatedPurchaseMetrics 
                : isStockBalance ? calculatedStockBalanceMetrics 
                : isStockLedger ? calculatedStockLedgerMetrics 
                : calculatedSalesMetrics;

  const topCards = isPurchase ? [
    { title: 'Total purchase invoice', value: metrics.purchaseAmount, pct: '+5.2%', color: '#3B82F6', icon: '🧾', format: 'currency' },
    { title: 'Total items', value: metrics.totalItems, pct: '+2.1%', color: '#3B82F6', icon: '📦', format: 'number' },
    { title: 'Submitted invoices', value: metrics.submittedCount, pct: '+12.4%', color: '#06D6A0', icon: '✅', format: 'number' },
    { title: 'Pending invoices', value: metrics.pendingCount, pct: '-1.5%', color: '#F43F5E', icon: '⏳', format: 'number' },
    { title: 'Total Suppliers', value: metrics.totalSuppliers, pct: '+3.4%', color: '#8B5CF6', icon: '🤝', format: 'number' }
  ] : isStockBalance ? [
    { title: 'Total Stock Value', value: metrics.totalValue, pct: '+2.4%', color: '#10B981', icon: '💎', format: 'currency' },
    { title: 'Total Items', value: metrics.totalItems, pct: '+1.1%', color: '#3B82F6', icon: '📦', format: 'number' },
    { title: 'Total Qty', value: metrics.totalQty, pct: '+5.8%', color: '#8B5CF6', icon: '⚖️', format: 'number' },
    { title: 'Low Stock Alerts', value: metrics.lowStockCount, pct: '-1.2%', color: '#F59E0B', icon: '⚠️', format: 'number' }
  ] : isStockLedger ? [
    { title: 'Total In (Qty)', value: metrics.inQty, pct: '+12.1%', color: '#06D6A0', icon: '📥', format: 'number' },
    { title: 'Total Out (Qty)', value: metrics.outQty, pct: '+8.4%', color: '#F43F5E', icon: '📤', format: 'number' },
    { title: 'Incoming Value', value: metrics.inValue, pct: '+10.2%', color: '#3B82F6', icon: '📈', format: 'currency' },
    { title: 'Transactions', value: metrics.totalTransactions, pct: '+5.0%', color: '#8B5CF6', icon: '🔄', format: 'number' }
  ] : [
    { title: 'Total revenue', value: metrics.salesAmount || '0', pct: '+18.5%', color: '#06D6A0', icon: '💳', format: 'currency' },
    { title: 'Total orders', value: metrics.totalOrders || '0', pct: '+12.8%', color: '#06D6A0', icon: '🛍️', format: 'number' },
    { title: 'New customers', value: metrics.newCustomers || '0', pct: '+8.2%', color: '#06D6A0', icon: '👥', format: 'number' },
    { title: 'Average order value', value: ((metrics.salesAmount ? parseInt((metrics.salesAmount || '').toString().replace(/[^0-9.-]+/g,'')) : 0) / Math.max(1, parseInt((metrics.totalOrders || '0').toString().replace(/,/g,'')))), pct: '-2.4%', color: '#FF6B6B', icon: '📈', format: 'currency' }
  ];

  const graphTitle = isPurchase ? 'Purchase overview' : isStockBalance ? 'Inventory Valuation' : isStockLedger ? 'Stock Movements' : 'Revenue overview';
  const graphSubtitle = isPurchase ? 'Track your spending and stock.' : isStockBalance ? 'Value of items currently in stock.' : isStockLedger ? 'Chronological flow of items.' : 'A good day starts with great numbers.';
  const mainAmount = isPurchase ? metrics.purchaseAmount : isStockBalance ? metrics.totalValue : isStockLedger ? metrics.inValue : metrics.salesAmount;
  const mainGraphColor = isPurchase ? '#3B82F6' : isStockBalance ? '#10B981' : isStockLedger ? '#8B5CF6' : 'var(--ap-primary-color)';

  const breakdownTitle = isPurchase ? 'Invoice status' : isStockBalance ? 'Stock Health' : isStockLedger ? 'Flow Volume' : 'Orders breakdown';
  const breakdownSubtitle = isPurchase ? 'Track submitted vs pending.' : isStockBalance ? 'Healthy vs Low Stock items.' : isStockLedger ? 'In vs Out quantities.' : 'Every order tells a story.';
  const breakdownTotal = isPurchase ? metrics.totalInvoices : isStockBalance ? metrics.totalItems : isStockLedger ? metrics.totalTransactions : metrics.totalOrders;
  const breakdownLabel = isPurchase ? 'Total invoices' : isStockBalance ? 'Total items' : isStockLedger ? 'Total entries' : 'Total orders';
  
  const breakdownItems = isPurchase ? [
    { label: 'Submitted', count: metrics.submittedCount, color: '#06D6A0' },
    { label: 'Pending', count: metrics.pendingCount, color: '#F43F5E' }
  ] : isStockBalance ? [
    { label: 'Healthy Stock', count: (metrics.totalItems || 0) - (metrics.lowStockCount || 0), color: '#06D6A0' },
    { label: 'Low Stock', count: metrics.lowStockCount || 0, color: '#F59E0B' }
  ] : isStockLedger ? [
    { label: 'Incoming', count: parseFloat(metrics.inQty) || 0, color: '#3B82F6' },
    { label: 'Outgoing', count: parseFloat(metrics.outQty) || 0, color: '#F43F5E' }
  ] : [
    { label: 'Dine-in', count: metrics.dineInCount, color: '#FF6B6B' },
    { label: 'Takeaway', count: metrics.takeawayCount, color: '#FFB703' },
    { label: 'Delivery', count: metrics.onlineCount, color: '#06D6A0' }
  ];

  const listTitle = isPurchase ? 'Top Purchased Items 📦' 
                  : isStockBalance ? 'Highest Valued Items 💎'
                  : isStockLedger ? 'Recent Movements 📉' 
                  : 'Most-loved dishes 🔥';
  const listItems = (isPurchase || isStockBalance || isStockLedger) ? metrics.trendingItems : metrics.trendingDishes;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', color: 'var(--ap-text-primary)', backgroundColor: 'transparent', padding: '24px', borderRadius: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: 'var(--ap-text-primary)' }}>Performance overview</h1>
          <select 
            value={dashboardViewType}
            onChange={(e) => setDashboardViewType(e.target.value)}
            style={{ background: 'var(--ap-card)', color: 'var(--ap-text-primary)', border: '1px solid var(--ap-border)', padding: '8px 16px', borderRadius: '8px', outline: 'none', fontWeight: '600' }}
          >
            <option value="salesreport">Sales</option>
            <option value="purchase">Purchase</option>
            <option value="salesinvoice">Sales Invoice</option>
            <option value="stockbalance">Stock Balance</option>
            <option value="stockledger">Stock Ledger</option>
          </select>
        </div>
        <div style={{ display: 'flex', background: 'var(--ap-card)', padding: '4px', borderRadius: '8px', border: '1px solid var(--ap-border)' }}>
          {['Today', 'Week', 'Month', 'Year'].map(tab => (
            <button
              key={tab}
              onClick={() => setTimeframe(tab)}
              style={{
                background: timeframe === tab ? 'var(--ap-border)' : 'transparent',
                color: timeframe === tab ? 'var(--ap-text-primary)' : 'var(--ap-text-secondary)',
                border: 'none', padding: '6px 20px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s', fontWeight: '600'
              }}
            >{tab}</button>
          ))}
        </div>
      </div>

      {/* Top Cards Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
        {topCards.map((card, i) => (
          <div key={i} style={{ background: 'var(--ap-card)', borderRadius: '16px', padding: '24px', border: '1px solid var(--ap-border)', display: 'flex', flexDirection: 'column', gap: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--ap-text-secondary)', fontSize: '14px', fontWeight: '600' }}>
              <span>{card.title}</span>
              <span style={{ width: '28px', height: '28px', background: 'var(--ap-bg)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>{card.icon}</span>
            </div>
            <h2 style={{ margin: 0, fontSize: '32px', fontWeight: '700', color: 'var(--ap-text-primary)' }}>
              <AnimatedNumber targetValue={card.value} format={card.format} dependency={dashboardViewType} />
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ background: `${card.color}20`, color: card.color, padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '700' }}>{card.pct}</span>
              <span style={{ color: 'var(--ap-text-secondary)', fontSize: '12px', fontWeight: '600', opacity: 0.7 }}>vs. last day</span>
              <div style={{ flex: 1, height: '24px', marginLeft: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                 <svg viewBox="0 0 100 30" style={{ width: '60px', height: '100%', overflow: 'visible' }}>
                   <path d={graphAnimating ? `M0,30 Q25,30 50,30 T100,30` : `M0,${20+Math.random()*10} Q25,${Math.random()*30} 50,${15+Math.random()*10} T100,${5+Math.random()*15}`} fill="none" stroke={card.color} strokeWidth="2.5" strokeLinecap="round" style={{ transition: 'd 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)' }} />
                 </svg>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Middle Row (Charts) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
        {/* Revenue Area Chart */}
        <div style={{ background: 'var(--ap-card)', borderRadius: '16px', padding: '32px', border: '1px solid var(--ap-border)', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', gridColumn: 'span 2' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--ap-text-primary)' }}>{graphTitle}</h3>
              <p style={{ margin: '6px 0 0', fontSize: '14px', color: 'var(--ap-text-secondary)' }}>{graphSubtitle}</p>
            </div>
            <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--ap-text-secondary)', fontWeight: '600' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--ap-primary-color)' }}/> Today</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--ap-border)' }}/> Previous period</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '40px' }}>
            <h2 style={{ margin: 0, fontSize: '36px', fontWeight: '800', color: 'var(--ap-text-primary)' }}>
              <AnimatedNumber targetValue={mainAmount} format="currency" dependency={dashboardViewType} />
            </h2>
            <span style={{ background: '#06D6A020', color: '#06D6A0', padding: '6px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: '700' }}>↗ 18.6%</span>
          </div>
          <div style={{ width: '100%', height: '240px', position: 'relative' }}>
            <svg viewBox="0 0 500 240" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
              <defs>
                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={mainGraphColor} stopOpacity="0.25"/>
                  <stop offset="100%" stopColor={mainGraphColor} stopOpacity="0"/>
                </linearGradient>
              </defs>
              <line x1="0" y1="40" x2="500" y2="40" stroke="var(--ap-border)" strokeDasharray="4 4" strokeWidth="1" />
              <line x1="0" y1="100" x2="500" y2="100" stroke="var(--ap-border)" strokeDasharray="4 4" strokeWidth="1" />
              <line x1="0" y1="160" x2="500" y2="160" stroke="var(--ap-border)" strokeDasharray="4 4" strokeWidth="1" />
              <line x1="0" y1="220" x2="500" y2="220" stroke="var(--ap-border)" strokeDasharray="4 4" strokeWidth="1" />
              <path d={graphAnimating ? flatPath : (metrics.prevGraphPath || '').replace(/120/g, '240')} fill="none" stroke="var(--ap-border)" strokeWidth="2" strokeDasharray="5 5" style={{ transition: 'd 1s cubic-bezier(0.16, 1, 0.3, 1)' }} />
              <path d={graphAnimating ? flatPath : (metrics.graphPath || '').replace(/120/g, '240')} fill="none" stroke={mainGraphColor} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'd 1s cubic-bezier(0.16, 1, 0.3, 1)' }} />
              <path d={graphAnimating ? `${flatPath} L500,240 L0,240 Z` : `${(metrics.graphPath || '').replace(/120/g, '240')} L500,240 L0,240 Z`} fill="url(#areaGrad)" style={{ transition: 'd 1s cubic-bezier(0.16, 1, 0.3, 1)' }} />
            </svg>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', color: 'var(--ap-text-secondary)', fontSize: '12px', fontWeight: '600' }}>
              <span>9 AM</span><span>11 AM</span><span>1 PM</span><span>3 PM</span><span>5 PM</span><span>7 PM</span><span>9 PM</span>
            </div>
          </div>
        </div>

        {/* Orders Breakdown */}
        <div style={{ background: 'var(--ap-card)', borderRadius: '16px', padding: '32px', border: '1px solid var(--ap-border)', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--ap-text-primary)' }}>{breakdownTitle}</h3>
              <p style={{ margin: '6px 0 0', fontSize: '14px', color: 'var(--ap-text-secondary)' }}>{breakdownSubtitle}</p>
            </div>
            <span style={{ color: 'var(--ap-text-secondary)', cursor: 'pointer', fontSize: '20px' }}>•••</span>
          </div>

          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '40px 0' }}>
            <div style={{ position: 'relative', width: '200px', height: '200px' }}>
              <svg viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                {isPurchase ? (
                  <>
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#06D6A0" strokeWidth="12" strokeDasharray={graphAnimating ? "0 300" : "190 70"} strokeDashoffset="0" strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s cubic-bezier(0.16, 1, 0.3, 1)' }} />
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#F43F5E" strokeWidth="12" strokeDasharray={graphAnimating ? "0 300" : "40 220"} strokeDashoffset="-200" strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s cubic-bezier(0.16, 1, 0.3, 1)' }} />
                  </>
                ) : (
                  <>
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#FF6B6B" strokeWidth="12" strokeDasharray={graphAnimating ? "0 300" : "140 130"} strokeDashoffset="0" strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s cubic-bezier(0.16, 1, 0.3, 1)' }} />
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#FFB703" strokeWidth="12" strokeDasharray={graphAnimating ? "0 300" : "60 210"} strokeDashoffset="-150" strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s cubic-bezier(0.16, 1, 0.3, 1)' }} />
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#06D6A0" strokeWidth="12" strokeDasharray={graphAnimating ? "0 300" : "38 230"} strokeDashoffset="-220" strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s cubic-bezier(0.16, 1, 0.3, 1)' }} />
                  </>
                )}
              </svg>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '32px', fontWeight: '800', color: 'var(--ap-text-primary)' }}>
                  <AnimatedNumber targetValue={breakdownTotal} format="number" dependency={dashboardViewType} />
                </h2>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--ap-text-secondary)', fontWeight: '600' }}>{breakdownLabel}</p>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {[...breakdownItems].map(t => {
              const total = parseInt((breakdownTotal || '').toString().replace(/,/g, '')) || 1;
              const pct = Math.round((t.count / total) * 100) || 0;
              return (
                <div key={t.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', fontWeight: '600' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '10px', height: '10px', background: t.color, borderRadius: '2px' }} />
                    <span style={{ color: 'var(--ap-text-secondary)' }}>{t.label}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '32px', color: 'var(--ap-text-primary)' }}>
                    <span style={{ width: '40px', textAlign: 'right' }}>
                      <AnimatedNumber targetValue={t.count} format="number" dependency={dashboardViewType} />
                    </span>
                    <span style={{ color: 'var(--ap-text-secondary)', width: '32px', textAlign: 'right', opacity: 0.8 }}>{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom Row - Removed Recent Orders, expanded dishes */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--ap-text-primary)' }}>{listTitle}</h3>
          <span style={{ color: 'var(--ap-text-secondary)', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>View menu ↗</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px' }}>
          {listItems.length > 0 ? listItems.slice(0, 4).map((dish, idx) => (
            <div key={idx} style={{ background: 'var(--ap-card)', borderRadius: '16px', padding: '20px', border: '1px solid var(--ap-border)', display: 'flex', alignItems: 'center', gap: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '12px', background: 'var(--ap-bg)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {dish.imageUrl ? <img src={dish.imageUrl} alt={dish.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '32px' }}>🍔</span>}
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: '700', color: 'var(--ap-text-primary)' }}>{dish.name}</h4>
                <p style={{ margin: '0 0 12px', fontSize: '12px', color: 'var(--ap-text-secondary)', fontWeight: '600' }}>{dish.realGroup || dish.classifiedLabel} · {dish.quantity} {dish.subLabel || (isPurchase ? 'bought' : 'sold')}</p>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <span style={{ fontSize: '15px', fontWeight: '800', color: 'var(--ap-text-primary)', display: 'inline-flex', alignItems: 'center' }}>
                    <CurrencySymbol currencyCode={activeCurrencyCode} size="1em" style={{ marginRight: '4px' }} />
                    {dish.amount || (parseFloat(dish.quantity || 0) * 14.5).toFixed(2)}
                  </span>
                  <span style={{ color: '#06D6A0', fontSize: '12px', fontWeight: '700' }}>{dish.pct || '↗ 12.4%'}</span>
                </div>
              </div>
            </div>
          )) : (
            <div style={{ color: 'var(--ap-text-secondary)', fontSize: '14px' }}>No top dishes today.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function AnimatedNumber({ targetValue, format, dependency }) {
  const [currentValue, setCurrentValue] = React.useState(0);
  
  React.useEffect(() => {
    let rawTarget = 0;
    if (typeof targetValue === 'string') {
      rawTarget = parseFloat((targetValue || '').toString().replace(/[^0-9.-]+/g, ''));
    } else if (typeof targetValue === 'number') {
      rawTarget = targetValue;
    }
    
    if (isNaN(rawTarget)) rawTarget = 0;

    let start = 0;
    const duration = 1200; // ms
    const startTime = performance.now();

    const animate = (time) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (easeOutQuart)
      const ease = 1 - Math.pow(1 - progress, 4);
      
      setCurrentValue(start + (rawTarget - start) * ease);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCurrentValue(rawTarget);
      }
    };
    
    requestAnimationFrame(animate);
  }, [targetValue, dependency]);

  const activeCurrencyCode = typeof resolveCurrencyCode === 'function' ? resolveCurrencyCode(null, localStorage.getItem('active_company'), localStorage.getItem('active_branch')) : 'USD';
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: format === 'currency' ? 2 : 0,
    maximumFractionDigits: format === 'currency' ? 2 : 0,
  });

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      {format === 'currency' && <CurrencySymbol currencyCode={activeCurrencyCode} size="1em" style={{ marginRight: '6px' }} />}
      {formatter.format(currentValue)}
    </span>
  );
}
